import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { useI18n } from "@/i18n";
import { useSettings } from "@/context/SettingsContext";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { compressImageToDataUrl } from "@/lib/image";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useTags } from "@/context/TagsContext";
import { TAG_COLORS, ICON_OPTIONS, TagIcon, tagChipStyle } from "@/lib/tagStyles";
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  PieChart, Pie, Cell, Legend,
} from "recharts";
import {
  Users, Clock, DollarSign, Coins, Plus, Pencil, Trash2, Download, Check, X, FileImage, Eye,
  ShieldCheck, UserPlus, AlertTriangle, Upload, Mail, Send, Megaphone, Ban, GripVertical,
  PieChart as PieChartIcon, Sparkles, Gift, TrendingDown, TrendingUp, FileText, RotateCcw, Recycle, MessageCircle, Bell,
  Settings, LayoutDashboard, ShoppingBag, Layers, HelpCircle, Server, Tag, Flower2, Star, BarChart3, Activity, Inbox, ChevronRight,
} from "lucide-react";

const CATS = ["daily_merit", "merit_project", "charity", "lotus", "service", "shop"];
// Categories offered when creating/editing going forward — 功德项目(merit_project) is
// merged into 每日功德(daily_merit), so it is no longer a picker option. Legacy
// merit_project items remain in the DB and are still shown/labelled.
const CAT_CREATE = ["daily_merit", "charity", "lotus", "service", "shop"];
const LOTUS_TYPES = ["", "large", "small108", "paper", "class", "blessing"];
const statusColor = { pending: "bg-amber-100 text-amber-800", approved: "bg-green-100 text-green-800", rejected: "bg-red-100 text-red-700" };

async function downloadFile(url, filename) {
  try {
    const res = await api.get(url, { responseType: "blob" });
    const blob = new Blob([res.data]);
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(link.href);
  } catch (e) { toast.error("Export failed"); }
}

/* ---------------- Data Analysis (数据分析) ---------------- */
const ELEMENT_COLORS = { "金": "#C9A227", "木": "#4E8D5B", "水": "#2E6F9E", "火": "#B23A2E", "土": "#9C6B3C" };
const ELEMENT_EN = { "金": "Metal", "木": "Wood", "水": "Water", "火": "Fire", "土": "Earth" };
const ZODIAC_EN = { "鼠": "Rat", "牛": "Ox", "虎": "Tiger", "兔": "Rabbit", "龙": "Dragon", "蛇": "Snake", "马": "Horse", "羊": "Goat", "猴": "Monkey", "鸡": "Rooster", "狗": "Dog", "猪": "Pig" };

/* ---------------- User Behaviour / Usage Analytics (用户行为) ---------------- */
const USAGE_RANGES = [
  { key: "today", zh: "今天", en: "Today" },
  { key: "7d", zh: "近7天", en: "7 Days" },
  { key: "30d", zh: "近30天", en: "30 Days" },
  { key: "all", zh: "全部", en: "All" },
];

function fmtDuration(sec, zh) {
  sec = Math.round(Number(sec) || 0);
  if (sec < 60) return zh ? `${sec}秒` : `${sec}s`;
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  if (m < 60) return zh ? `${m}分${s}秒` : `${m}m ${s}s`;
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return zh ? `${h}时${mm}分` : `${h}h ${mm}m`;
}

function fmtWhen(iso, zh) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(zh ? "zh-CN" : "en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch (e) { return iso; }
}

function RankBar({ label, value, max, suffix }) {
  const pct = max > 0 ? Math.max(4, Math.round((value / max) * 100)) : 0;
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-sm">
        <span className="truncate pr-2 text-foreground">{label}</span>
        <span className="shrink-0 tabular-nums text-muted-foreground">{suffix}</span>
      </div>
      <div className="h-2 rounded-full bg-[hsl(var(--brand-gold)/0.12)] overflow-hidden">
        <div className="h-full rounded-full bg-[hsl(var(--brand-gold))]" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function UserUsageDialog({ userId, range, onClose }) {
  const { lang } = useI18n();
  const zh = lang === "zh";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId) return;
    setLoading(true);
    api.get(`/admin/analytics/user/${userId}`, { params: { range } })
      .then((r) => setData(r.data))
      .catch(() => toast.error(zh ? "加载失败" : "Failed to load"))
      .finally(() => setLoading(false));
  }, [userId, range, zh]);

  return (
    <Dialog open={!!userId} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="usage-user-dialog">
        <DialogHeader>
          <DialogTitle>
            {zh ? "用户行为详情" : "User Activity"}
            {data?.user?.name ? ` · ${data.user.name}` : ""}
          </DialogTitle>
        </DialogHeader>
        {loading || !data ? (
          <div className="py-10 text-center text-muted-foreground text-sm">{zh ? "加载中…" : "Loading…"}</div>
        ) : (
          <div className="space-y-5">
            <div className="text-sm text-muted-foreground">{data.user.email}</div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[
                { label: zh ? "访问页数" : "Visits", value: data.summary.visits },
                { label: zh ? "会话数" : "Sessions", value: data.summary.sessions },
                { label: zh ? "总停留" : "Dwell", value: fmtDuration(data.summary.total_dwell_sec, zh) },
                { label: zh ? "功能次数" : "Features", value: data.summary.features },
              ].map((k, i) => (
                <div key={i} className="rounded-lg border border-border p-3" data-testid={`usage-user-kpi-${i}`}>
                  <div className="text-xs text-muted-foreground">{k.label}</div>
                  <div className="text-lg font-semibold tabular-nums">{k.value}</div>
                </div>
              ))}
            </div>

            <div>
              <div className="text-sm font-semibold mb-2">{zh ? "页面停留" : "Pages"}</div>
              {data.pages.length === 0 ? (
                <div className="text-sm text-muted-foreground">{zh ? "暂无数据" : "No data"}</div>
              ) : (
                <div className="space-y-3">
                  {data.pages.map((p, i) => (
                    <RankBar key={i} label={p.label} value={p.views}
                      max={data.pages[0].views}
                      suffix={`${p.views} · ${fmtDuration(p.avg_sec, zh)}`} />
                  ))}
                </div>
              )}
            </div>

            <div>
              <div className="text-sm font-semibold mb-2">{zh ? "使用功能" : "Features Used"}</div>
              {data.features.length === 0 ? (
                <div className="text-sm text-muted-foreground">{zh ? "暂无数据" : "No data"}</div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {data.features.map((f, i) => (
                    <Badge key={i} variant="outline" className="text-xs" data-testid={`usage-user-feature-${i}`}>
                      {f.label} · {f.count}
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            <div>
              <div className="text-sm font-semibold mb-2">{zh ? "最近会话" : "Recent Sessions"}</div>
              {data.sessions.length === 0 ? (
                <div className="text-sm text-muted-foreground">{zh ? "暂无数据" : "No data"}</div>
              ) : (
                <div className="space-y-1.5">
                  {data.sessions.slice(0, 10).map((s, i) => (
                    <div key={i} className="flex items-center justify-between text-sm border-b border-border/60 pb-1.5">
                      <span className="text-muted-foreground">{fmtWhen(s.start, zh)}</span>
                      <span className="tabular-nums">{s.views} {zh ? "页" : "pages"} · {fmtDuration(s.total_sec, zh)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function UsageAnalytics() {
  const { lang } = useI18n();
  const zh = lang === "zh";
  const [range, setRange] = useState("7d");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drillUser, setDrillUser] = useState(null);
  const [retention, setRetention] = useState(null);
  const [churn, setChurn] = useState(null);

  const load = useCallback((r) => {
    setLoading(true);
    api.get("/admin/analytics/usage", { params: { range: r } })
      .then((res) => setData(res.data))
      .catch(() => toast.error(zh ? "加载失败" : "Failed to load"))
      .finally(() => setLoading(false));
  }, [zh]);

  useEffect(() => { load(range); }, [range, load]);

  // Range-independent datasets (loaded once)
  useEffect(() => {
    api.get("/admin/analytics/retention", { params: { days: 30 } })
      .then((res) => setRetention(res.data)).catch(() => {});
    api.get("/admin/analytics/churn", { params: { days: 14 } })
      .then((res) => setChurn(res.data)).catch(() => {});
  }, []);

  const exportCsv = useCallback(() => {
    if (!data) return;
    const rows = [];
    const push = (...cells) => rows.push(cells);
    push(zh ? "用户行为报告" : "User Behaviour Report");
    push(zh ? "时间范围" : "Range", data.range);
    push(zh ? "生成时间" : "Generated", data.generated_at);
    push("");
    push(zh ? "概览" : "Summary");
    const s = data.summary;
    push(zh ? "活跃用户" : "Active Users", s.active_users);
    push(zh ? "新增用户" : "New Users", s.new_users);
    push(zh ? "会话数" : "Sessions", s.sessions);
    push(zh ? "页面浏览" : "Page Views", s.page_views);
    push(zh ? "平均停留(秒)" : "Avg Session (s)", s.avg_session_sec);
    push(zh ? "总停留(秒)" : "Total Dwell (s)", s.total_dwell_sec);
    push("");
    push(zh ? "每日趋势" : "Daily Series", zh ? "活跃" : "Active", zh ? "会话" : "Sessions", zh ? "浏览" : "Views");
    (data.series || []).forEach((d) => push(d.date, d.active_users, d.sessions, d.page_views));
    push("");
    push(zh ? "页面热度" : "Page Heat", zh ? "浏览" : "Views", zh ? "平均停留(秒)" : "Avg (s)");
    (data.page_heat || []).forEach((p) => push(p.label, p.views, p.avg_sec));
    push("");
    push(zh ? "功能使用" : "Feature Usage", zh ? "次数" : "Count", zh ? "人数" : "Users");
    (data.feature_usage || []).forEach((f) => push(f.label, f.count, f.users));
    push("");
    push(zh ? "转化漏斗" : "Funnel", zh ? "人数" : "Users", "% top", "% prev");
    (data.funnel || []).forEach((f) => push(zh ? f.label_zh : f.label_en, f.users, f.pct_of_top, f.pct_of_prev));
    push("");
    push(zh ? "用户明细" : "Users", zh ? "邮箱" : "Email", zh ? "访问" : "Visits", zh ? "会话" : "Sessions", zh ? "停留(秒)" : "Dwell(s)", zh ? "功能" : "Features", zh ? "最近活跃" : "Last Seen");
    (data.users || []).forEach((u) => push(u.name, u.email, u.visits, u.sessions, u.total_sec, u.features, u.last_seen));
    if (churn) {
      push("");
      push(zh ? `流失会员(>${churn.days}天未活跃)` : `Inactive (>${churn.days}d)`, zh ? "邮箱" : "Email", zh ? "未活跃天数" : "Days Inactive", zh ? "最近活跃" : "Last Seen");
      (churn.members || []).forEach((m) => push(m.name, m.email, m.inactive_days, m.last_seen));
    }
    const csv = rows.map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `ciao_usage_${data.range}.csv`;
    document.body.appendChild(link); link.click(); link.remove();
    URL.revokeObjectURL(link.href);
  }, [data, churn, zh]);

  const printReport = useCallback(() => {
    if (!data) return;
    const esc = (v) => String(v ?? "").replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));
    const s = data.summary;
    const sumRows = [
      [zh ? "活跃用户" : "Active Users", s.active_users],
      [zh ? "新增用户" : "New Users", s.new_users],
      [zh ? "会话数" : "Sessions", s.sessions],
      [zh ? "页面浏览" : "Page Views", s.page_views],
      [zh ? "平均停留" : "Avg Session", fmtDuration(s.avg_session_sec, zh)],
      [zh ? "总停留时长" : "Total Dwell", fmtDuration(s.total_dwell_sec, zh)],
    ].map(([k, v]) => `<div class="kpi"><span>${esc(k)}</span><b>${esc(v)}</b></div>`).join("");
    const tbl = (head, body) => `<table><thead><tr>${head.map((h) => `<th>${esc(h)}</th>`).join("")}</tr></thead><tbody>${body}</tbody></table>`;
    const pageBody = (data.page_heat || []).map((p) => `<tr><td>${esc(p.label)}</td><td>${p.views}</td><td>${fmtDuration(p.avg_sec, zh)}</td></tr>`).join("");
    const featBody = (data.feature_usage || []).map((f) => `<tr><td>${esc(f.label)}</td><td>${f.count}</td><td>${f.users}</td></tr>`).join("");
    const funnelBody = (data.funnel || []).map((f) => `<tr><td>${esc(zh ? f.label_zh : f.label_en)}</td><td>${f.users}</td><td>${f.pct_of_top}%</td><td>${f.pct_of_prev}%</td></tr>`).join("");
    const churnBody = churn ? (churn.members || []).map((m) => `<tr><td>${esc(m.name)}</td><td>${esc(m.email)}</td><td>${m.inactive_days}</td></tr>`).join("") : "";
    const win = window.open("", "_blank");
    if (!win) { toast.error(zh ? "请允许弹出窗口" : "Allow pop-ups"); return; }
    win.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>${zh ? "用户行为报告" : "User Behaviour Report"}</title>
      <style>body{font-family:'Noto Sans SC',Arial,sans-serif;color:#2b2b2b;padding:28px;}
      h1{font-size:20px;margin:0 0 4px} .meta{color:#888;font-size:12px;margin-bottom:18px}
      h2{font-size:15px;margin:22px 0 8px;border-bottom:2px solid #C9A227;padding-bottom:4px}
      .kpis{display:flex;flex-wrap:wrap;gap:10px} .kpi{border:1px solid #e3d9c2;border-radius:8px;padding:8px 12px;min-width:120px;display:flex;flex-direction:column}
      .kpi span{font-size:11px;color:#999} .kpi b{font-size:16px}
      table{width:100%;border-collapse:collapse;font-size:12px;margin-top:6px} th,td{border:1px solid #e5e5e5;padding:6px 8px;text-align:left} th{background:#faf6ec}
      </style></head><body>
      <h1>${zh ? "你好功德 · 用户行为报告" : "CiAO Merit · User Behaviour"}</h1>
      <div class="meta">${zh ? "范围" : "Range"}: <b>${esc(data.range)}</b> · ${zh ? "生成时间" : "Generated"}: ${esc(data.generated_at)}</div>
      <h2>${zh ? "概览" : "Summary"}</h2><div class="kpis">${sumRows}</div>
      <h2>${zh ? "转化漏斗" : "Funnel"}</h2>${tbl([zh ? "环节" : "Step", zh ? "人数" : "Users", "% top", "% prev"], funnelBody)}
      <h2>${zh ? "页面热度" : "Page Heat"}</h2>${tbl([zh ? "页面" : "Page", zh ? "浏览" : "Views", zh ? "平均停留" : "Avg"], pageBody)}
      <h2>${zh ? "功能使用" : "Feature Usage"}</h2>${tbl([zh ? "功能" : "Feature", zh ? "次数" : "Count", zh ? "人数" : "Users"], featBody)}
      ${churn && churn.count ? `<h2>${zh ? `流失会员 (>${churn.days}天未活跃)` : `Inactive (>${churn.days}d)`}</h2>${tbl([zh ? "姓名" : "Name", zh ? "邮箱" : "Email", zh ? "未活跃天数" : "Days"], churnBody)}` : ""}
      </body></html>`);
    win.document.close();
    setTimeout(() => { win.focus(); win.print(); }, 350);
  }, [data, churn, zh]);

  const s = data?.summary;
  const kpis = s ? [
    { label: zh ? "活跃用户" : "Active Users", value: s.active_users, icon: Users },
    { label: zh ? "新增用户" : "New Users", value: s.new_users, icon: UserPlus },
    { label: zh ? "会话数" : "Sessions", value: s.sessions, icon: TrendingUp },
    { label: zh ? "页面浏览" : "Page Views", value: s.page_views, icon: Eye },
    { label: zh ? "平均停留" : "Avg. Session", value: fmtDuration(s.avg_session_sec, zh), icon: Clock },
    { label: zh ? "总停留时长" : "Total Dwell", value: fmtDuration(s.total_dwell_sec, zh), icon: Clock },
  ] : [];

  const series = (data?.series || []).map((d) => ({ ...d, label: d.date.slice(5) }));
  const maxPageViews = Math.max(1, ...(data?.page_heat || []).map((p) => p.views));
  const maxFeature = Math.max(1, ...(data?.feature_usage || []).map((f) => f.count));

  return (
    <div className="space-y-6 mt-10 pt-8 border-t border-border" data-testid="admin-usage">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold">{zh ? "用户行为分析" : "User Behaviour"}</h3>
          <p className="text-sm text-muted-foreground">{zh ? "每日活跃、停留时长与功能使用" : "Daily activity, dwell time & feature usage"}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex rounded-lg border border-border p-1 bg-card">
            {USAGE_RANGES.map((r) => (
              <button
                key={r.key}
                data-testid={`usage-range-${r.key}`}
                onClick={() => setRange(r.key)}
                className={`px-3 py-1.5 text-sm rounded-md transition-colors ${range === r.key ? "bg-[hsl(var(--brand-gold)/0.18)] text-[hsl(var(--brand-ink))] font-semibold" : "text-muted-foreground hover:text-foreground"}`}
              >
                {zh ? r.zh : r.en}
              </button>
            ))}
          </div>
          <Button variant="outline" size="sm" onClick={exportCsv} disabled={!data} data-testid="usage-download">
            <Download className="w-4 h-4 mr-1.5" />{zh ? "导出CSV" : "CSV"}
          </Button>
          <Button variant="outline" size="sm" onClick={printReport} disabled={!data} data-testid="usage-pdf">
            <FileText className="w-4 h-4 mr-1.5" />{zh ? "打印PDF" : "PDF"}
          </Button>
        </div>
      </div>

      {loading && !data ? (
        <div className="py-16 text-center text-muted-foreground text-sm" data-testid="usage-loading">{zh ? "加载中…" : "Loading…"}</div>
      ) : !data ? null : (
        <>
          {/* KPI cards */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {kpis.map((k, i) => (
              <Card key={i} className="border-border" data-testid={`usage-kpi-${i}`}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1">
                    <k.icon className="w-3.5 h-3.5" /> {k.label}
                  </div>
                  <div className="text-xl font-semibold tabular-nums">{k.value}</div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Activity trend */}
          <Card className="border-border" data-testid="usage-trend-chart">
            <CardContent className="p-4">
              <div className="text-sm font-semibold mb-3">{zh ? "活跃趋势" : "Activity Trend"}</div>
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={series} margin={{ top: 6, right: 12, left: -16, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="active_users" name={zh ? "活跃用户" : "Active Users"} stroke="#C9A227" strokeWidth={2} dot={{ r: 2 }} />
                  <Line type="monotone" dataKey="page_views" name={zh ? "页面浏览" : "Page Views"} stroke="#2E6F9E" strokeWidth={2} dot={{ r: 2 }} />
                  <Line type="monotone" dataKey="sessions" name={zh ? "会话" : "Sessions"} stroke="#B23A2E" strokeWidth={2} dot={{ r: 2 }} />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Page heat + Feature usage */}
          <div className="grid lg:grid-cols-2 gap-4">
            <Card className="border-border" data-testid="usage-page-heat">
              <CardContent className="p-4">
                <div className="text-sm font-semibold mb-3">{zh ? "页面热度（浏览 · 平均停留）" : "Page Heat (views · avg dwell)"}</div>
                {(data.page_heat || []).length === 0 ? (
                  <div className="py-10 text-center text-muted-foreground text-sm">{zh ? "暂无数据" : "No data"}</div>
                ) : (
                  <div className="space-y-3">
                    {data.page_heat.slice(0, 10).map((p, i) => (
                      <RankBar key={i} label={p.label} value={p.views} max={maxPageViews}
                        suffix={`${p.views} · ${fmtDuration(p.avg_sec, zh)}`} />
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="border-border" data-testid="usage-feature-usage">
              <CardContent className="p-4">
                <div className="text-sm font-semibold mb-3">{zh ? "功能使用次数" : "Feature Usage"}</div>
                {(data.feature_usage || []).length === 0 ? (
                  <div className="py-10 text-center text-muted-foreground text-sm">{zh ? "暂无数据" : "No data"}</div>
                ) : (
                  <div className="space-y-3">
                    {data.feature_usage.map((f, i) => (
                      <RankBar key={i} label={f.label} value={f.count} max={maxFeature}
                        suffix={`${f.count}${zh ? "次" : ""} · ${f.users}${zh ? "人" : "u"}`} />
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Conversion funnel */}
          <Card className="border-border" data-testid="usage-funnel">
            <CardContent className="p-4">
              <div className="text-sm font-semibold mb-1">{zh ? "功能转化漏斗" : "Conversion Funnel"}</div>
              <p className="text-xs text-muted-foreground mb-3">{zh ? "从打开运程到完成下单的各环节人数与流失" : "From viewing fortune to completing an order"}</p>
              {(data.funnel || []).every((f) => f.users === 0) ? (
                <div className="py-10 text-center text-muted-foreground text-sm">{zh ? "该时段暂无转化数据" : "No conversion data in this range"}</div>
              ) : (
                <div className="space-y-3">
                  {(data.funnel || []).map((f, i) => {
                    const topUsers = (data.funnel[0] || {}).users || 0;
                    const pct = topUsers > 0 ? Math.max(3, Math.round((f.users / topUsers) * 100)) : 0;
                    return (
                      <div key={f.key} data-testid={`usage-funnel-step-${i}`}>
                        <div className="flex items-center justify-between text-sm mb-1">
                          <span className="text-foreground">{zh ? f.label_zh : f.label_en}</span>
                          <span className="tabular-nums text-muted-foreground">
                            {f.users}{zh ? "人" : ""}
                            {i > 0 && <span className="ml-2 text-xs">({zh ? "较上一步" : "vs prev"} {f.pct_of_prev}%)</span>}
                          </span>
                        </div>
                        <div className="h-6 rounded-md bg-[hsl(var(--brand-gold)/0.1)] overflow-hidden">
                          <div className="h-full rounded-md bg-[hsl(var(--brand-gold)/0.75)] flex items-center justify-end pr-2 text-[11px] font-medium text-[hsl(var(--brand-ink))]"
                            style={{ width: `${pct}%` }}>
                            {f.pct_of_top}%
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Retention + Churn */}
          <div className="grid lg:grid-cols-2 gap-4">
            <Card className="border-border" data-testid="usage-retention">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-1">
                  <div className="text-sm font-semibold">{zh ? "新用户留存" : "New-user Retention"}</div>
                  {retention?.overall && (
                    <div className="flex gap-3 text-xs">
                      <span className="text-muted-foreground">D1 <b className="text-foreground">{retention.overall.d1_rate}%</b></span>
                      <span className="text-muted-foreground">D7 <b className="text-foreground">{retention.overall.d7_rate}%</b></span>
                    </div>
                  )}
                </div>
                <p className="text-xs text-muted-foreground mb-3">{zh ? "注册后次日(D1)与7日内(D7)的回访比例" : "Return next day (D1) and within a week (D7)"}</p>
                {!retention ? (
                  <div className="py-10 text-center text-muted-foreground text-sm">{zh ? "加载中…" : "Loading…"}</div>
                ) : (retention.cohorts || []).length === 0 ? (
                  <div className="py-10 text-center text-muted-foreground text-sm">{zh ? "数据积累中（暂无新用户）" : "Collecting data (no new users yet)"}</div>
                ) : (
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={retention.cohorts.map((c) => ({ ...c, label: c.date.slice(5) }))} margin={{ top: 6, right: 8, left: -18, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                      <YAxis unit="%" domain={[0, 100]} tick={{ fontSize: 11 }} />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="d1_rate" name={zh ? "次日 D1" : "D1"} fill="#C9A227" radius={[3, 3, 0, 0]} />
                      <Bar dataKey="d7_rate" name={zh ? "7日 D7" : "D7"} fill="#2E6F9E" radius={[3, 3, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>

            <Card className="border-border" data-testid="usage-churn">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-1">
                  <div className="text-sm font-semibold flex items-center gap-1.5">
                    <AlertTriangle className="w-4 h-4 text-[hsl(var(--brand-gold))]" />
                    {zh ? "流失提醒" : "Churn Alert"}
                  </div>
                  {churn && (
                    <Badge variant="outline" className="text-xs" data-testid="usage-churn-count">
                      {churn.count} {zh ? "人" : ""}
                    </Badge>
                  )}
                </div>
                <p className="text-xs text-muted-foreground mb-3">{zh ? `超过 ${churn?.days || 14} 天未活跃的会员` : `Members inactive for ${churn?.days || 14}+ days`}</p>
                {!churn ? (
                  <div className="py-10 text-center text-muted-foreground text-sm">{zh ? "加载中…" : "Loading…"}</div>
                ) : (churn.members || []).length === 0 ? (
                  <div className="py-10 text-center text-muted-foreground text-sm">{zh ? "太好了，暂无流失会员" : "Great — no inactive members"}</div>
                ) : (
                  <div className="max-h-[220px] overflow-y-auto divide-y divide-border/60">
                    {churn.members.map((m, i) => (
                      <div key={m.user_id} className="flex items-center justify-between py-2 cursor-pointer hover:bg-muted/40 px-1 rounded"
                        onClick={() => setDrillUser(m.user_id)} data-testid={`usage-churn-row-${i}`}>
                        <div className="min-w-0">
                          <div className="font-medium text-sm truncate">{m.name || "—"}</div>
                          <div className="text-xs text-muted-foreground truncate">{m.email}</div>
                        </div>
                        <div className="text-right shrink-0 ml-2">
                          <div className="text-sm font-semibold tabular-nums text-[hsl(var(--brand-vermilion,var(--destructive)))]">{m.inactive_days}{zh ? "天" : "d"}</div>
                          <div className="text-[11px] text-muted-foreground">{m.ever_active ? (zh ? "最近" : "last") : (zh ? "未活跃" : "never")} {m.ever_active ? fmtWhen(m.last_seen, zh) : ""}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Per-user table (drill-down) */}
          <Card className="border-border" data-testid="usage-users-table">
            <CardContent className="p-4">
              <div className="text-sm font-semibold mb-3">{zh ? "用户明细（点击查看详情）" : "Per-user (click to drill down)"}</div>
              {(data.users || []).length === 0 ? (
                <div className="py-10 text-center text-muted-foreground text-sm">{zh ? "该时段暂无用户活动" : "No user activity in this range"}</div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{zh ? "用户" : "User"}</TableHead>
                        <TableHead className="text-right">{zh ? "访问" : "Visits"}</TableHead>
                        <TableHead className="text-right">{zh ? "会话" : "Sessions"}</TableHead>
                        <TableHead className="text-right">{zh ? "停留" : "Dwell"}</TableHead>
                        <TableHead className="text-right">{zh ? "功能" : "Features"}</TableHead>
                        <TableHead className="text-right">{zh ? "最近活跃" : "Last Seen"}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.users.map((u, i) => (
                        <TableRow key={u.user_id} className="cursor-pointer hover:bg-muted/50"
                          onClick={() => setDrillUser(u.user_id)} data-testid={`usage-user-row-${i}`}>
                          <TableCell>
                            <div className="font-medium">{u.name || "—"}</div>
                            <div className="text-xs text-muted-foreground">{u.email}</div>
                          </TableCell>
                          <TableCell className="text-right tabular-nums">{u.visits}</TableCell>
                          <TableCell className="text-right tabular-nums">{u.sessions}</TableCell>
                          <TableCell className="text-right tabular-nums">{fmtDuration(u.total_sec, zh)}</TableCell>
                          <TableCell className="text-right tabular-nums">{u.features}</TableCell>
                          <TableCell className="text-right text-xs text-muted-foreground">{fmtWhen(u.last_seen, zh)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {drillUser && (
        <UserUsageDialog userId={drillUser} range={range} onClose={() => setDrillUser(null)} />
      )}
    </div>
  );
}


function Insights() {
  const { lang } = useI18n();
  const [range, setRange] = useState("all");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tsStats, setTsStats] = useState(null);
  const [reminding, setReminding] = useState(false);
  const [remindLog, setRemindLog] = useState([]);

  const loadRemindLog = useCallback(() => {
    api.get("/admin/taisui-remind-log")
      .then((r) => setRemindLog(r.data.items || []))
      .catch(() => { /* non-blocking */ });
  }, []);

  useEffect(() => {
    api.get("/admin/taisui-stats", { params: { lang } })
      .then((r) => setTsStats(r.data))
      .catch(() => { /* non-blocking */ });
    loadRemindLog();
  }, [lang, loadRemindLog]);

  const remindOffenders = async () => {
    setReminding(true);
    try {
      const r = await api.post("/admin/taisui-remind", null, { params: { lang } });
      const d = r.data || {};
      if (!d.email_configured) {
        toast.error(lang === "zh" ? "邮件服务未配置，无法发送提醒" : "Email service not configured");
      } else if (d.offenders === 0) {
        toast.info(lang === "zh" ? "目前没有犯太岁的会员" : "No offending members right now");
      } else {
        toast.success(lang === "zh"
          ? `已向 ${d.sent}/${d.offenders} 位犯太岁会员发送提醒`
          : `Reminder sent to ${d.sent}/${d.offenders} offending members`);
      }
      loadRemindLog();
    } catch (e) {
      toast.error(lang === "zh" ? "发送失败，请稍后再试" : "Failed to send");
    } finally {
      setReminding(false);
    }
  };

  const load = useCallback((r) => {
    setLoading(true);
    api.get("/admin/insights", { params: { range: r } })
      .then((res) => setData(res.data))
      .catch(() => toast.error(lang === "zh" ? "加载失败" : "Failed to load"))
      .finally(() => setLoading(false));
  }, [lang]);

  useEffect(() => { load(range); }, [range, load]);

  const RANGES = [
    { key: "week", zh: "本周", en: "This Week" },
    { key: "month", zh: "本月", en: "This Month" },
    { key: "all", zh: "全部", en: "All Time" },
  ];

  const downloadReport = () => {
    if (!data) return;
    const rows = [];
    rows.push([lang === "zh" ? "CiAO 功德 · 数据分析报告" : "CiAO Merit · Data Analysis Report"]);
    rows.push([lang === "zh" ? "范围" : "Range", (RANGES.find((x) => x.key === data.range) || {})[lang === "zh" ? "zh" : "en"] || data.range]);
    rows.push([lang === "zh" ? "生成时间" : "Generated", data.generated_at]);
    rows.push([]);
    rows.push([lang === "zh" ? "会员总数" : "Members", data.members_total, lang === "zh" ? "已建档" : "Profiled", data.members_profiled, lang === "zh" ? "新增" : "New", data.new_members]);
    rows.push([]);
    rows.push([lang === "zh" ? "五行分布" : "Five Elements"]);
    (data.element_dist || []).forEach((e) => rows.push([lang === "zh" ? e.name : ELEMENT_EN[e.name] || e.name, e.value]));
    rows.push([]);
    rows.push([lang === "zh" ? "生肖分布" : "Zodiac"]);
    (data.zodiac_dist || []).forEach((z) => rows.push([lang === "zh" ? z.name : ZODIAC_EN[z.name] || z.name, z.value]));
    rows.push([]);
    rows.push([lang === "zh" ? "会员等级" : "Member tier", lang === "zh" ? "人数" : "Members"]);
    (data.member_tiers || []).forEach((tt) => rows.push([lang === "zh" ? tt.label_zh : tt.label_en, tt.count]));
    rows.push([]);
    rows.push([lang === "zh" ? "功德统计" : "Merit"]);
    rows.push([lang === "zh" ? "发放点数" : "Points issued", data.merit.points_issued, lang === "zh" ? "消耗点数" : "Points redeemed", data.merit.points_redeemed, lang === "zh" ? "核准订单" : "Approved orders", data.merit.approved_orders, lang === "zh" ? "总流水RM" : "Revenue RM", data.merit.revenue_total]);
    rows.push([]);
    rows.push([lang === "zh" ? "类目" : "Category", lang === "zh" ? "订单" : "Orders", lang === "zh" ? "点数" : "Points", lang === "zh" ? "现金RM" : "Cash RM"]);
    (data.merit.categories || []).forEach((c) => rows.push([lang === "zh" ? c.label_zh : (c.label_en || c.category), c.orders, c.points, c.cash]));
    rows.push([]);
    rows.push([lang === "zh" ? "项目金额区间" : "Amount range", lang === "zh" ? "订单" : "Orders", lang === "zh" ? "现金RM" : "Cash RM"]);
    (data.merit.amount_buckets || []).forEach((b) => rows.push([b.label, b.orders, b.cash]));
    rows.push([]);
    rows.push([lang === "zh" ? "项目金额排名" : "Revenue by project", lang === "zh" ? "现金RM" : "Cash RM", lang === "zh" ? "订单" : "Orders"]);
    (data.merit.revenue_by_project || []).forEach((p) => rows.push([p.title, p.cash, p.orders]));
    const csv = rows.map((r) => r.map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `ciao_insights_${data.range}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(link.href);
    toast.success(lang === "zh" ? "报告已下载" : "Report downloaded");
  };

  const printReport = () => {
    if (!data) return;
    const zh = lang === "zh";
    const rangeLabel = (RANGES.find((x) => x.key === data.range) || {})[zh ? "zh" : "en"] || data.range;
    const esc = (s) => String(s ?? "").replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));
    const rows = (arr) => arr.map((r) => `<tr>${r.map((c, i) => `<td class="${i === 0 ? "l" : "r"}">${esc(c)}</td>`).join("")}</tr>`).join("");
    const m = data.merit || {};
    const elementsTbl = rows((data.element_dist || []).map((e) => [zh ? e.name : (ELEMENT_EN[e.name] || e.name), e.value]));
    const zodiacTbl = rows((data.zodiac_dist || []).filter((z) => z.value > 0).map((z) => [zh ? z.name : (ZODIAC_EN[z.name] || z.name), z.value]));
    const tiersTbl = rows((data.member_tiers || []).map((tt) => [zh ? tt.label_zh : tt.label_en, tt.count]));
    const catTbl = rows((m.categories || []).map((c) => [zh ? c.label_zh : (c.label_en || c.category), c.orders, c.points, `RM${Number(c.cash).toFixed(2)}`]));
    const bucketTbl = rows((m.amount_buckets || []).map((b) => [b.label, b.orders, `RM${Number(b.cash).toFixed(2)}`]));
    const revTbl = rows((m.revenue_by_project || []).map((p) => [p.title, `RM${Number(p.cash).toFixed(2)}`, p.orders]));
    const T = zh ? {
      title: "你好功德 · 数据分析报告", range: "范围", gen: "生成时间", members: "会员",
      profiled: "已建档", nw: "新增", el: "五行分布", zo: "生肖分布", tiers: "会员功德等级",
      merit: "功德统计", issued: "发放点数", redeemed: "消耗点数", ords: "核准订单", rev: "总流水",
      cat: "类目", orders: "订单", points: "点数", cash: "现金", amount: "项目金额区间",
      revproj: "项目金额排名", tier: "等级", count: "人数", el_h: "五行", zo_h: "生肖", print: "打印 / 存为 PDF",
    } : {
      title: "CiAO Merit · Data Analysis Report", range: "Range", gen: "Generated", members: "Members",
      profiled: "Profiled", nw: "New", el: "Five Elements", zo: "Zodiac", tiers: "Member Tiers",
      merit: "Merit", issued: "Points issued", redeemed: "Points redeemed", ords: "Approved orders", rev: "Revenue",
      cat: "Category", orders: "Orders", points: "Points", cash: "Cash", amount: "Project Amount",
      revproj: "Revenue by project", tier: "Tier", count: "Members", el_h: "Element", zo_h: "Zodiac", print: "Print / Save as PDF",
    };
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>${esc(T.title)}</title>
      <style>
        *{box-sizing:border-box} body{font-family:-apple-system,"Noto Serif SC","Microsoft YaHei",system-ui,sans-serif;color:#2a231b;margin:0;padding:32px 40px;background:#fff}
        h1{font-size:22px;margin:0 0 4px;color:#8a6a2f}
        .meta{color:#6b6157;font-size:12px;margin-bottom:16px}
        .kpis{display:flex;gap:10px;flex-wrap:wrap;margin:12px 0 8px}
        .kpi{border:1px solid #e7ddc9;border-radius:10px;padding:10px 14px;min-width:150px}
        .kpi .l{font-size:11px;color:#8a7a5f} .kpi .v{font-size:20px;font-weight:800;color:#b23a2e}
        h2{font-size:14px;margin:20px 0 8px;color:#2a231b;border-left:4px solid #C9A227;padding-left:8px}
        .grid{display:flex;gap:18px;flex-wrap:wrap}
        table{border-collapse:collapse;width:100%;font-size:12px;margin-bottom:6px}
        .half{flex:1;min-width:240px}
        th,td{border-bottom:1px solid #eee;padding:6px 8px;text-align:left}
        td.r,th.r{text-align:right} th{color:#8a7a5f;font-weight:600;background:#faf6ee}
        .foot{margin-top:22px;color:#9a8f80;font-size:11px;text-align:center;border-top:1px solid #eee;padding-top:10px}
        @media print{.noprint{display:none} body{padding:16px}}
      </style></head><body>
      <button class="noprint" onclick="window.print()" style="float:right;background:#C9A227;color:#2a231b;border:none;border-radius:8px;padding:8px 14px;font-weight:700;cursor:pointer">${esc(T.print)}</button>
      <h1>${esc(T.title)}</h1>
      <div class="meta">${esc(T.range)}: <b>${esc(rangeLabel)}</b> · ${esc(T.gen)}: ${esc(data.generated_at)}</div>
      <div class="kpis">
        <div class="kpi"><div class="l">${esc(T.members)} (${esc(T.profiled)})</div><div class="v">${data.members_profiled}/${data.members_total}</div></div>
        <div class="kpi"><div class="l">${esc(T.issued)}</div><div class="v">${Number(m.points_issued||0).toFixed(2)}</div></div>
        <div class="kpi"><div class="l">${esc(T.redeemed)}</div><div class="v">${Number(m.points_redeemed||0).toFixed(2)}</div></div>
        <div class="kpi"><div class="l">${esc(T.ords)}</div><div class="v">${m.approved_orders||0}</div></div>
        <div class="kpi"><div class="l">${esc(T.rev)}</div><div class="v">RM${Number(m.revenue_total||0).toFixed(2)}</div></div>
      </div>
      <div class="grid">
        <div class="half"><h2>${esc(T.el)}</h2><table><tr><th>${esc(T.el_h)}</th><th class="r">${esc(T.count)}</th></tr>${elementsTbl}</table></div>
        <div class="half"><h2>${esc(T.zo)}</h2><table><tr><th>${esc(T.zo_h)}</th><th class="r">${esc(T.count)}</th></tr>${zodiacTbl || `<tr><td colspan=2>-</td></tr>`}</table></div>
      </div>
      <div class="grid">
        <div class="half"><h2>${esc(T.tiers)}</h2><table><tr><th>${esc(T.tier)}</th><th class="r">${esc(T.count)}</th></tr>${tiersTbl}</table></div>
        <div class="half"><h2>${esc(T.amount)}</h2><table><tr><th>${esc(T.amount)}</th><th class="r">${esc(T.orders)}</th><th class="r">${esc(T.cash)}</th></tr>${bucketTbl}</table></div>
      </div>
      <h2>${esc(T.merit)} — ${esc(T.cat)}</h2>
      <table><tr><th>${esc(T.cat)}</th><th class="r">${esc(T.orders)}</th><th class="r">${esc(T.points)}</th><th class="r">${esc(T.cash)}</th></tr>${catTbl || `<tr><td colspan=4>-</td></tr>`}</table>
      <h2>${esc(T.revproj)}</h2>
      <table><tr><th>${esc(T.revproj)}</th><th class="r">${esc(T.cash)}</th><th class="r">${esc(T.orders)}</th></tr>${revTbl || `<tr><td colspan=3>-</td></tr>`}</table>
      <div class="foot">你好功德 CiAO Merit</div>
      <script>window.onload=function(){setTimeout(function(){window.print()},350)}</script>
      </body></html>`;
    const w = window.open("", "_blank");
    if (!w) { toast.error(lang === "zh" ? "请允许弹出窗口以打印/导出 PDF" : "Please allow pop-ups to print/export PDF"); return; }
    w.document.open();
    w.document.write(html);
    w.document.close();
  };

  const elementData = (data?.element_dist || []).map((e) => ({ ...e, en: ELEMENT_EN[e.name] || e.name }));
  const zodiacData = (data?.zodiac_dist || []).map((z) => ({ ...z, label: lang === "zh" ? z.name : ZODIAC_EN[z.name] || z.name }));
  const catData = (data?.merit?.categories || []).map((c) => ({ ...c, name: lang === "zh" ? c.label_zh : (c.label_en || c.category) }));
  const tierData = (data?.member_tiers || []).map((t) => ({ ...t, name: lang === "zh" ? t.label_zh : t.label_en }));
  const revProjData = (data?.merit?.revenue_by_project || []).map((p) => ({ ...p, name: p.title }));
  const bucketData = (data?.merit?.amount_buckets || []).map((b) => ({ ...b, name: b.label }));
  const elementTotal = elementData.reduce((s, e) => s + e.value, 0);
  const TIER_COLORS = ["#9C6B3C", "#2E6F9E", "#C9A227", "#B23A2E"];

  const kpis = data ? [
    { icon: Users, label: lang === "zh" ? "已建档会员" : "Profiled members", value: `${data.members_profiled}/${data.members_total}` },
    { icon: Sparkles, label: lang === "zh" ? "发放功德点" : "Points issued", value: Number(data.merit.points_issued).toFixed(2) },
    { icon: Gift, label: lang === "zh" ? "消耗功德点" : "Points redeemed", value: Number(data.merit.points_redeemed).toFixed(2) },
    { icon: TrendingUp, label: lang === "zh" ? "核准订单" : "Approved orders", value: data.merit.approved_orders },
  ] : [];

  return (
    <div className="space-y-6" data-testid="admin-insights">
      {/* Header: range filter + download */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className="grid h-8 w-8 place-items-center rounded-full bg-[hsl(var(--brand-gold)/0.15)] text-[hsl(var(--brand-bronze))]"><PieChartIcon className="h-4 w-4" /></span>
          <div>
            <div className="font-display text-lg font-semibold">{lang === "zh" ? "数据分析" : "Data Analysis"}</div>
            <div className="text-xs text-muted-foreground">{lang === "zh" ? "五行 · 生肖 · 功德（仅后台查看/下载）" : "Elements · Zodiac · Merit (admin view/download only)"}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-lg border border-border p-1" role="group">
            {RANGES.map((r) => (
              <button
                key={r.key}
                data-testid={`insights-range-${r.key}`}
                onClick={() => setRange(r.key)}
                className={`px-3 py-1.5 text-sm rounded-md transition-colors ${range === r.key ? "bg-[hsl(var(--brand-gold)/0.18)] text-[hsl(var(--brand-ink))] font-semibold" : "text-muted-foreground hover:text-foreground"}`}
              >
                {lang === "zh" ? r.zh : r.en}
              </button>
            ))}
          </div>
          <Button variant="outline" size="sm" onClick={downloadReport} disabled={!data} data-testid="insights-download">
            <Download className="h-4 w-4 mr-1.5" />{lang === "zh" ? "下载报告" : "Download"}
          </Button>
          <Button variant="outline" size="sm" onClick={printReport} disabled={!data} data-testid="insights-pdf">
            <FileText className="h-4 w-4 mr-1.5" />PDF
          </Button>
        </div>
      </div>

      {loading || !data ? (
        <div className="py-16 text-center text-muted-foreground">{lang === "zh" ? "加载中…" : "Loading…"}</div>
      ) : (
        <>
          {/* KPI cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {kpis.map((c, i) => (
              <Card key={i} className="border-border" data-testid={`insights-kpi-${i}`}><CardContent className="p-4">
                <div className="flex items-center gap-2 text-[hsl(var(--brand-bronze))] mb-2"><c.icon className="h-4 w-4" /><span className="text-xs">{c.label}</span></div>
                <div className="font-mono-num text-2xl font-bold">{c.value}</div>
              </CardContent></Card>
            ))}
          </div>

          <div className="grid lg:grid-cols-2 gap-4">
            {/* Five Elements donut */}
            <Card className="border-border" data-testid="insights-element-chart"><CardContent className="p-4">
              <div className="font-semibold mb-1">{lang === "zh" ? "五行分布" : "Five Elements"}</div>
              <p className="text-xs text-muted-foreground mb-2">{lang === "zh" ? "按会员日主五行统计" : "By member day-master element"}</p>
              {elementTotal === 0 ? (
                <div className="py-16 text-center text-muted-foreground text-sm">{lang === "zh" ? "暂无已建档会员数据" : "No profiled members yet"}</div>
              ) : (
                <ResponsiveContainer width="100%" height={260}>
                  <PieChart>
                    <Pie data={elementData} dataKey="value" nameKey={lang === "zh" ? "name" : "en"} cx="50%" cy="50%" innerRadius={58} outerRadius={92} paddingAngle={2}>
                      {elementData.map((e) => <Cell key={e.name} fill={ELEMENT_COLORS[e.name] || "#999"} />)}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </CardContent></Card>

            {/* Zodiac bar */}
            <Card className="border-border" data-testid="insights-zodiac-chart"><CardContent className="p-4">
              <div className="font-semibold mb-1">{lang === "zh" ? "生肖分布" : "Zodiac"}</div>
              <p className="text-xs text-muted-foreground mb-2">{lang === "zh" ? "按会员出生年生肖统计" : "By member birth-year zodiac"}</p>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={zodiacData} margin={{ top: 6, right: 8, bottom: 0, left: -20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} interval={0} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                  <Tooltip cursor={{ fill: "hsl(var(--brand-gold)/0.08)" }} />
                  <Bar dataKey="value" fill="hsl(var(--brand-bronze))" radius={[5, 5, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent></Card>
          </div>

          {/* Merit by category + top projects */}
          <div className="grid lg:grid-cols-2 gap-4">
            <Card className="border-border" data-testid="insights-merit-cat"><CardContent className="p-4">
              <div className="font-semibold mb-3">{lang === "zh" ? "功德类目（核准订单）" : "Merit by category (approved)"}</div>
              {catData.length === 0 ? (
                <div className="py-14 text-center text-muted-foreground text-sm">{lang === "zh" ? "该时段暂无核准订单" : "No approved orders in this range"}</div>
              ) : (
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={catData} margin={{ top: 6, right: 8, bottom: 0, left: -20 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                    <Tooltip cursor={{ fill: "hsl(var(--brand-gold)/0.08)" }} />
                    <Bar dataKey="orders" name={lang === "zh" ? "订单" : "Orders"} fill="hsl(var(--brand-gold))" radius={[5, 5, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </CardContent></Card>

            <Card className="border-border" data-testid="insights-top-projects"><CardContent className="p-4">
              <div className="font-semibold mb-3">{lang === "zh" ? "热门功德项目 Top" : "Top merit projects"}</div>
              {(data.merit.top_projects || []).length === 0 ? (
                <div className="py-14 text-center text-muted-foreground text-sm">{lang === "zh" ? "暂无数据" : "No data"}</div>
              ) : (
                <div className="space-y-2">
                  {(data.merit.top_projects || []).map((p, i) => (
                    <div key={i} className="flex items-center gap-3" data-testid={`insights-top-${i}`}>
                      <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-[hsl(var(--brand-gold)/0.16)] text-xs font-bold text-[hsl(var(--brand-bronze))]">{i + 1}</span>
                      <span className="flex-1 truncate text-sm">{p.title}</span>
                      <span className="text-xs text-muted-foreground mr-2 font-mono-num">RM{Number(p.cash || 0).toFixed(0)}</span>
                      <span className="font-mono-num text-sm font-semibold text-[hsl(var(--brand-vermillion))]">{p.orders}</span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent></Card>
          </div>

          {/* Member tiers + project amount */}
          <div className="grid lg:grid-cols-2 gap-4">
            <Card className="border-border" data-testid="insights-member-tiers"><CardContent className="p-4">
              <div className="font-semibold mb-1">{lang === "zh" ? "会员功德等级" : "Member Tiers"}</div>
              <p className="text-xs text-muted-foreground mb-2">{lang === "zh" ? "按当前功德点数余额划分" : "By current merit-point balance"}</p>
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={tierData} margin={{ top: 6, right: 8, bottom: 0, left: -20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                  <Tooltip cursor={{ fill: "hsl(var(--brand-gold)/0.08)" }} />
                  <Bar dataKey="count" name={lang === "zh" ? "人数" : "Members"} radius={[5, 5, 0, 0]}>
                    {tierData.map((t, i) => <Cell key={t.key} fill={TIER_COLORS[i % TIER_COLORS.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </CardContent></Card>

            <Card className="border-border" data-testid="insights-amount-buckets"><CardContent className="p-4">
              <div className="font-semibold mb-1">{lang === "zh" ? "项目金额分布" : "Project Amount"}</div>
              <p className="text-xs text-muted-foreground mb-2">
                {lang === "zh" ? `按核准订单金额区间（总流水 RM${Number(data.merit.revenue_total || 0).toFixed(0)}）` : `Approved order amount ranges (revenue RM${Number(data.merit.revenue_total || 0).toFixed(0)})`}
              </p>
              {(data.merit.approved_orders || 0) === 0 ? (
                <div className="py-14 text-center text-muted-foreground text-sm">{lang === "zh" ? "该时段暂无核准订单" : "No approved orders in this range"}</div>
              ) : (
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={bucketData} margin={{ top: 6, right: 8, bottom: 0, left: -20 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                    <Tooltip cursor={{ fill: "hsl(var(--brand-gold)/0.08)" }} />
                    <Bar dataKey="orders" name={lang === "zh" ? "订单" : "Orders"} fill="hsl(var(--brand-bronze))" radius={[5, 5, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </CardContent></Card>
          </div>

          {/* Revenue by project */}
          <Card className="border-border" data-testid="insights-revenue-project"><CardContent className="p-4">
            <div className="font-semibold mb-3">{lang === "zh" ? "项目金额排名（现金流水）" : "Revenue by project (cash)"}</div>
            {revProjData.length === 0 ? (
              <div className="py-10 text-center text-muted-foreground text-sm">{lang === "zh" ? "该时段暂无数据" : "No data in this range"}</div>
            ) : (
              <ResponsiveContainer width="100%" height={Math.max(160, revProjData.length * 38)}>
                <BarChart data={revProjData} layout="vertical" margin={{ top: 4, right: 16, bottom: 4, left: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                  <YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                  <Tooltip cursor={{ fill: "hsl(var(--brand-gold)/0.08)" }} formatter={(v) => `RM${Number(v).toFixed(2)}`} />
                  <Bar dataKey="cash" name={lang === "zh" ? "现金 RM" : "Cash RM"} fill="hsl(var(--brand-gold))" radius={[0, 5, 5, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent></Card>

          {/* 犯太岁统计 / Tai Sui offending stats */}
          {tsStats && (
            <Card className="border-border" data-testid="insights-taisui"><CardContent className="p-4">
              <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
                <div className="font-semibold">
                  {lang === "zh" ? `犯太岁统计 · ${tsStats.year_info.year} ${tsStats.year_info.ganzhi}（${tsStats.year_info.zodiac_zh}年）` : `Tai Sui offenders · ${tsStats.year_info.year} ${tsStats.year_info.ganzhi}`}
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-[11px]">{lang === "zh" ? `已分析会员 ${tsStats.analyzed} 人` : `${tsStats.analyzed} analyzed`}</Badge>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button size="sm" variant="outline" className="h-8" disabled={reminding || tsStats.offenders === 0} data-testid="taisui-remind-btn">
                        <Bell className="h-3.5 w-3.5 mr-1" />{lang === "zh" ? "提醒犯太岁会员" : "Remind offenders"}
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent data-testid="taisui-remind-dialog">
                      <AlertDialogHeader>
                        <AlertDialogTitle>{lang === "zh" ? "发送犯太岁提醒？" : "Send Tai Sui reminders?"}</AlertDialogTitle>
                        <AlertDialogDescription>
                          {lang === "zh"
                            ? `将向 ${tsStats.year_info.year} 年犯太岁的 ${tsStats.offenders} 位会员发送一封温馨提醒邮件，内含化太岁祈福配套链接。`
                            : `A gentle reminder email (with the Tai Sui blessing package link) will be sent to the ${tsStats.offenders} members who offend Tai Sui in ${tsStats.year_info.year}.`}
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>{lang === "zh" ? "取消" : "Cancel"}</AlertDialogCancel>
                        <AlertDialogAction onClick={remindOffenders} data-testid="taisui-remind-confirm">{lang === "zh" ? "确认发送" : "Send"}</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
              {tsStats.analyzed === 0 ? (
                <div className="py-8 text-center text-muted-foreground text-sm">{lang === "zh" ? "暂无完整生辰资料的会员" : "No members with complete birth data"}</div>
              ) : (
                <div className="grid sm:grid-cols-3 gap-4 items-center">
                  <div className="text-center sm:border-r border-border">
                    <div className="text-4xl font-bold font-mono-num text-[hsl(var(--brand-vermillion))]" data-testid="taisui-stat-pct">{tsStats.offending_percentage}%</div>
                    <div className="text-xs text-muted-foreground mt-1">{lang === "zh" ? `会员犯太岁（${tsStats.offenders}/${tsStats.analyzed}）` : `offend Tai Sui (${tsStats.offenders}/${tsStats.analyzed})`}</div>
                  </div>
                  <div className="sm:col-span-2 grid grid-cols-2 gap-x-4 gap-y-1.5">
                    <div className="col-span-2 text-[11px] font-semibold text-muted-foreground">{lang === "zh" ? "按犯太岁类型（人数）" : "By relationship type"}</div>
                    {tsStats.type_breakdown.map((tb) => (
                      <div key={tb.code} className="flex items-center justify-between text-sm" data-testid={`taisui-type-${tb.code}`}>
                        <span>{tb.label}</span>
                        <span className="font-mono-num font-semibold">{tb.count}</span>
                      </div>
                    ))}
                    <div className="col-span-2 mt-2 text-[11px] font-semibold text-muted-foreground">{lang === "zh" ? "按犯太岁柱（人数）" : "By pillar"}</div>
                    {tsStats.pillar_breakdown.map((pb) => (
                      <div key={pb.pillar} className="flex items-center justify-between text-sm" data-testid={`taisui-pillar-stat-${pb.pillar}`}>
                        <span>{pb.label}</span>
                        <span className="font-mono-num font-semibold">{pb.count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <p className="text-[11px] text-muted-foreground mt-3">{lang === "zh" ? "从八字四柱地支判断，涵盖值/冲/刑/害/破；一名会员可同时命中多种类型。" : "Based on the four-pillar branches (same/clash/punishment/harm/break); a member may match multiple types."}</p>
              {remindLog.length > 0 && (
                <div className="mt-3 border-t border-border pt-3" data-testid="taisui-remind-log">
                  <div className="text-[11px] font-semibold text-muted-foreground mb-1.5">{lang === "zh" ? "提醒发送记录" : "Reminder history"}</div>
                  <div className="space-y-1">
                    {remindLog.slice(0, 5).map((r) => (
                      <div key={r.id} className="flex items-center justify-between text-xs" data-testid={`taisui-remind-log-${r.id}`}>
                        <span className="text-muted-foreground">
                          {new Date(r.created_at).toLocaleString(lang === "zh" ? "zh-CN" : "en-US")}
                          <span className="ml-2 opacity-70">{r.trigger === "cron-renewal" ? (lang === "zh" ? "自动" : "Auto") : (lang === "zh" ? "手动" : "Manual")}</span>
                        </span>
                        <span className="font-mono-num">{lang === "zh" ? `已发送 ${r.sent} / ${r.total_members} 人` : `sent ${r.sent}/${r.total_members}`}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent></Card>
          )}
        </>
      )}
    </div>
  );
}
function Overview({ onNavigate }) {
  const { t, lang } = useI18n();
  const [a, setA] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  useEffect(() => { api.get("/admin/analytics").then((r) => setA(r.data)).catch(() => {}); }, []);
  if (!a) return <div className="py-10 text-center text-muted-foreground">{t("common.loading")}</div>;
  const cards = [
    { icon: Users, label: t("admin.members_count"), value: a.members },
    { icon: Clock, label: t("admin.pending"), value: a.pending_orders },
    { icon: DollarSign, label: t("admin.revenue"), value: `RM${a.revenue.toFixed(2)}` },
    { icon: Coins, label: t("admin.pointsIssued"), value: a.points_issued.toFixed(2) },
  ];
  const catData = (a.category_stats || []).map((c) => ({ name: t(`categories.${c.category}`) || c.category, revenue: c.revenue }));
  return (
    <div className="space-y-6">
      {/* key summary */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((c, i) => (
          <Card key={i} className="border-border" data-testid={`admin-stat-${i}`}><CardContent className="p-4">
            <div className="flex items-center gap-2 text-[hsl(var(--brand-bronze))] mb-2"><c.icon className="h-4 w-4" /><span className="text-xs">{c.label}</span></div>
            <div className="font-mono-num text-2xl font-bold">{c.value}</div>
          </CardContent></Card>
        ))}
      </div>

      {/* one key trend */}
      <Card className="border-border"><CardContent className="p-4">
        <div className="font-semibold mb-3">{t("admin.dailyRevenue")}</div>
        <ResponsiveContainer width="100%" height={240}>
          <LineChart data={a.daily}><CartesianGrid strokeDasharray="3 3" stroke="#e7ddc9" /><XAxis dataKey="date" fontSize={11} /><YAxis fontSize={12} /><Tooltip /><Line type="monotone" dataKey="revenue" stroke="hsl(6,72%,45%)" strokeWidth={2} /></LineChart>
        </ResponsiveContainer>
      </CardContent></Card>

      {/* quick actions to deeper data */}
      <div className="flex flex-wrap gap-2">
        <Button variant="outline" size="sm" onClick={() => onNavigate && onNavigate("insights")} data-testid="overview-goto-insights">
          <BarChart3 className="h-4 w-4 mr-1.5" />{lang === "zh" ? "查看完整分析" : "Full analysis"}
        </Button>
        <Button variant="outline" size="sm" onClick={() => onNavigate && onNavigate("usage")} data-testid="overview-goto-usage">
          <Activity className="h-4 w-4 mr-1.5" />{lang === "zh" ? "使用情况" : "Usage"}
        </Button>
        <Button variant="ghost" size="sm" onClick={() => setShowDetail((v) => !v)} data-testid="overview-toggle-detail">
          {showDetail ? (lang === "zh" ? "收起详情" : "Hide details") : (lang === "zh" ? "查看详情" : "View details")}
          <ChevronRight className={`h-4 w-4 ml-1 transition-transform ${showDetail ? "rotate-90" : ""}`} />
        </Button>
      </div>

      {/* collapsible detailed section */}
      {showDetail && (
        <div className="space-y-6" data-testid="overview-detail">
          <Card className="border-border"><CardContent className="p-4">
            <div className="font-semibold mb-3">{t("admin.revenueByCat")}</div>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={catData}><CartesianGrid strokeDasharray="3 3" stroke="#e7ddc9" /><XAxis dataKey="name" fontSize={12} /><YAxis fontSize={12} /><Tooltip /><Bar dataKey="revenue" fill="hsl(38,52%,46%)" radius={[6, 6, 0, 0]} /></BarChart>
            </ResponsiveContainer>
          </CardContent></Card>

          <Card className="border-border" data-testid="admin-allocation-card"><CardContent className="p-4">
            <div className="font-semibold">{t("adminx.allocation")}</div>
            <p className="text-xs text-muted-foreground mt-1 mb-3">{t("adminx.allocationDesc")}</p>
            {(a.monthly || []).length === 0 ? (
              <div className="py-8 text-center text-muted-foreground text-sm">{t("adminx.allocationEmpty")}</div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t("adminx.month")}</TableHead>
                      <TableHead className="text-right">{t("adminx.monthTotal")}</TableHead>
                      <TableHead className="text-right">{t("adminx.rewardPool")}</TableHead>
                      <TableHead className="text-right">{t("adminx.teamFund")}</TableHead>
                      <TableHead className="text-right">{t("adminx.platformMgmt")}</TableHead>
                      <TableHead className="text-right">{t("adminx.projectCost")}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(a.monthly || []).map((m) => (
                      <TableRow key={m.month} data-testid={`allocation-row-${m.month}`}>
                        <TableCell className="font-mono-num font-semibold">{m.month}</TableCell>
                        <TableCell className="text-right font-mono-num font-bold">RM{Number(m.revenue).toFixed(2)}</TableCell>
                        <TableCell className="text-right font-mono-num text-[hsl(var(--brand-vermillion))]">RM{Number(m.reward_pool).toFixed(2)}</TableCell>
                        <TableCell className="text-right font-mono-num">RM{Number(m.team_fund).toFixed(2)}</TableCell>
                        <TableCell className="text-right font-mono-num">RM{Number(m.platform).toFixed(2)}</TableCell>
                        <TableCell className="text-right font-mono-num text-[hsl(var(--brand-bronze))]">RM{Number(m.project_cost).toFixed(2)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent></Card>
        </div>
      )}
    </div>
  );
}

/* ---------------- Orders ---------------- */
function Orders({ onChanged }) {
  const { t, lang } = useI18n();
  const [orders, setOrders] = useState([]);
  const [status, setStatus] = useState("pending");
  const [receipt, setReceipt] = useState(null);
  const [uploadOrder, setUploadOrder] = useState(null);
  const [rData, setRData] = useState("");
  const [rRef, setRRef] = useState("");
  const [rBusy, setRBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/orders", { params: status === "all" ? {} : { status } }).then((r) => setOrders(r.data)).catch(() => {});
  }, [status]);
  useEffect(() => { load(); }, [load]);

  const act = async (id, st) => {
    try { await api.put(`/admin/orders/${id}/status`, { status: st }); toast.success(t(`orderStatus.${st}`)); load(); onChanged && onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const openUpload = (o) => { setUploadOrder(o); setRData(o.receipt_data || ""); setRRef(o.bank_reference || ""); };
  const onReceiptFile = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 8 * 1024 * 1024) { toast.error(lang === "zh" ? "图片需小于 8MB" : "Image must be < 8MB"); return; }
    try { setRData(await compressImageToDataUrl(f)); }
    catch { toast.error(lang === "zh" ? "读取失败" : "Read failed"); }
    e.target.value = "";
  };
  const saveReceipt = async () => {
    if (!rData) { toast.error(lang === "zh" ? "请先选择付款证明图片" : "Select a receipt image first"); return; }
    setRBusy(true);
    try {
      await api.put(`/admin/orders/${uploadOrder.id}/receipt`, { receipt_data: rData, bank_reference: rRef });
      toast.success(t("adminx.receiptSaved"));
      setUploadOrder(null); setRData(""); setRRef(""); load(); onChanged && onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setRBusy(false); }
  };

  return (
    <div>
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div className="flex gap-2">
          {["pending", "approved", "rejected", "all"].map((s) => (
            <Button key={s} size="sm" variant={status === s ? "default" : "outline"}
              className={status === s ? "bg-[hsl(var(--primary))] text-primary-foreground" : ""}
              onClick={() => setStatus(s)} data-testid={`orders-filter-${s}`}>
              {s === "all" ? (lang === "zh" ? "全部" : "All") : t(`orderStatus.${s}`)}
            </Button>
          ))}
        </div>
        <Button variant="outline" onClick={() => downloadFile("/admin/export/orders", "all_orders.xlsx")} data-testid="export-orders-button">
          <Download className="h-4 w-4 mr-1" /> {t("admin.export")}
        </Button>
      </div>
      <Card className="border-border"><CardContent className="p-0 overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>{t("auth.name")}</TableHead><TableHead>{lang === "zh" ? "项目" : "Project"}</TableHead>
            <TableHead>{t("common.amount")}</TableHead><TableHead>{lang === "zh" ? "收据" : "Receipt"}</TableHead>
            <TableHead>{t("common.status")}</TableHead><TableHead className="text-right">{lang === "zh" ? "操作" : "Action"}</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {orders.map((o) => (
              <TableRow key={o.id} data-testid={`admin-order-row-${o.id}`}>
                <TableCell><div className="font-semibold">{o.participant_name || o.user_name}</div><div className="text-xs text-muted-foreground">{o.user_email}</div></TableCell>
                <TableCell className="text-sm">{o.project_title}</TableCell>
                <TableCell className="font-mono-num text-sm">RM{Number(o.cash_amount).toFixed(2)}{o.points_used > 0 && <div className="text-[10px] text-muted-foreground">+{o.points_used}pts</div>}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-1">
                    {o.receipt_data ? (
                      <>
                        <Button variant="ghost" size="sm" onClick={() => setReceipt(o.receipt_data)} data-testid={`view-receipt-${o.id}`}><Eye className="h-4 w-4" /></Button>
                        {o.receipt_by_admin && <Badge variant="outline" className="text-[10px] px-1">{lang === "zh" ? "后台补" : "by admin"}</Badge>}
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-[hsl(var(--brand-bronze))]" title={t("adminx.replaceReceipt")} onClick={() => openUpload(o)} data-testid={`replace-receipt-${o.id}`}><Upload className="h-4 w-4" /></Button>
                      </>
                    ) : (
                      <Button variant="outline" size="sm" className="h-8 border-[hsl(var(--brand-gold))]" onClick={() => openUpload(o)} data-testid={`upload-receipt-${o.id}`}><Upload className="h-3.5 w-3.5 mr-1" />{t("adminx.uploadReceipt")}</Button>
                    )}
                  </div>
                </TableCell>
                <TableCell><Badge className={statusColor[o.status]}>{t(`orderStatus.${o.status}`)}</Badge></TableCell>
                <TableCell className="text-right">
                  {o.status === "pending" && (
                    <div className="flex justify-end gap-1">
                      <Button size="icon" variant="outline" className="h-8 w-8 text-green-700 border-green-300" onClick={() => act(o.id, "approved")} data-testid={`admin-remittance-approve-button-${o.id}`}><Check className="h-4 w-4" /></Button>
                      <Button size="icon" variant="outline" className="h-8 w-8 text-red-600 border-red-300" onClick={() => act(o.id, "rejected")} data-testid={`admin-remittance-reject-button-${o.id}`}><X className="h-4 w-4" /></Button>
                    </div>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {orders.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">{lang === "zh" ? "暂无订单" : "No orders"}</TableCell></TableRow>}
          </TableBody>
        </Table>
      </CardContent></Card>
      <Dialog open={!!receipt} onOpenChange={() => setReceipt(null)}>
        <DialogContent className="max-w-lg bg-card"><DialogHeader><DialogTitle>{t("admin.viewReceipt")}</DialogTitle></DialogHeader>
          {receipt && <img src={receipt} alt="receipt" className="w-full rounded-lg border border-border" />}
        </DialogContent>
      </Dialog>

      <Dialog open={!!uploadOrder} onOpenChange={(o) => { if (!o) { setUploadOrder(null); setRData(""); setRRef(""); } }}>
        <DialogContent className="max-w-lg bg-card max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{t("adminx.uploadReceipt")}</DialogTitle></DialogHeader>
          {uploadOrder && (
            <div className="space-y-3">
              <div className="rounded-lg bg-[hsl(var(--secondary))] p-3 text-sm">
                <div className="font-semibold">{uploadOrder.participant_name || uploadOrder.user_name} · {uploadOrder.project_title}</div>
                <div className="text-xs text-muted-foreground">{uploadOrder.user_email} · RM{Number(uploadOrder.cash_amount).toFixed(2)}</div>
              </div>
              <p className="text-xs text-muted-foreground">{t("adminx.uploadReceiptDesc")}</p>
              <div><Label>{t("adminx.receiptImage")}</Label><input type="file" accept="image/*" onChange={onReceiptFile} className="block w-full text-sm mt-1" data-testid="admin-receipt-file" /></div>
              {rData && <img src={rData} alt="" className="w-full rounded-lg border border-border max-h-64 object-contain bg-black/5" data-testid="admin-receipt-preview" />}
              <div><Label>{t("adminx.bankRef")}</Label><Input value={rRef} onChange={(e) => setRRef(e.target.value)} placeholder={lang === "zh" ? "如：转账参考号/银行" : "e.g. transfer ref / bank"} data-testid="admin-receipt-ref" /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setUploadOrder(null); setRData(""); setRRef(""); }}>{t("common.cancel")}</Button>
            <Button className="bg-[hsl(var(--primary))] text-primary-foreground" disabled={rBusy} onClick={saveReceipt} data-testid="admin-receipt-save">{t("common.save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ---------------- Projects ---------------- */
// Resize + compress an image File to a JPEG data URL (keeps base64 small and
// durable for storage in Mongo). Falls back to the raw data URL on any error.
const emptyProject = { category: "daily_merit", title_zh: "", title_en: "", desc_zh: "", desc_en: "", price: 0, image_url: "", promoted: false, active: true, lotus_type: "", whatsapp_group_link: "", element: "", elements: [], subcategory: "", stock: "", sort_order: 0 };
const FALLBACK_IMG = "data:image/svg+xml;utf8," + encodeURIComponent(
  "<svg xmlns='http://www.w3.org/2000/svg' width='96' height='96'><rect width='100%' height='100%' fill='hsl(36,28%,90%)'/><text x='50%' y='55%' font-size='30' fill='hsl(38,52%,46%)' text-anchor='middle' font-family='serif'>功</text></svg>"
);
// Preset sub-category tags per main category (admin may also type a custom tag)
const PRESET_TAGS = {
  daily_merit: ["供灯", "放生", "印经", "施食", "香供", "布施", "诵经"],
  merit_project: ["供灯", "放生", "印经", "施食", "香供"],
  charity: ["助学", "施医", "赈灾", "养老", "孤儿", "扶贫"],
  lotus: ["消灾", "祈福", "超度", "祈愿", "还愿"],
  service: ["咨询", "法会", "开光", "择日"],
  shop: ["佛珠", "香品", "摆件", "佛像", "挂饰", "护身"],
};
function ProjectManager({ fixedCategory }) {
  const { t, lang } = useI18n();
  const { tags: allTags } = useTags();
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [catFilter, setCatFilter] = useState(fixedCategory || "all");
  const [dragId, setDragId] = useState(null);
  const [overId, setOverId] = useState(null);
  const load = useCallback(() => { api.get("/projects", { params: { include_inactive: true } }).then((r) => setItems(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);
  const effectiveCat = fixedCategory || catFilter;
  const shown = (effectiveCat === "all" ? items : items.filter((p) => p.category === effectiveCat))
    .slice().sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
  // show a legacy 功德项目 filter chip only if such items still exist
  const filterCats = CAT_CREATE.concat(items.some((p) => p.category === "merit_project") ? ["merit_project"] : []);
  // sub-category suggestions for the item being edited: presets + already-used tags in that category
  const tagSuggestions = (cat) => {
    const used = items.filter((p) => p.category === cat && p.subcategory).map((p) => p.subcategory);
    const managed = allTags.filter((tg) => tg.category === cat).map((tg) => tg.name);
    return Array.from(new Set([...(PRESET_TAGS[cat] || []), ...managed, ...used]));
  };

  const reorderTo = async (targetId) => {
    const fromId = dragId;
    setDragId(null); setOverId(null);
    if (!fromId || fromId === targetId) return;
    const list = [...shown];
    const from = list.findIndex((p) => p.id === fromId);
    const to = list.findIndex((p) => p.id === targetId);
    if (from < 0 || to < 0) return;
    const [moved] = list.splice(from, 1);
    list.splice(to, 0, moved);
    const orders = list.map((p, i) => ({ id: p.id, sort_order: (i + 1) * 10 }));
    const map = new Map(orders.map((o) => [o.id, o.sort_order]));
    setItems((prev) => prev.map((p) => (map.has(p.id) ? { ...p, sort_order: map.get(p.id) } : p)));
    try { await api.put("/projects/reorder", { orders }); toast.success(lang === "zh" ? "顺序已更新" : "Order updated"); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); load(); }
  };

  const save = async () => {
    const payload = {
      ...editing,
      price: Number(editing.price) || 0,
      sort_order: Number(editing.sort_order) || 0,
      element: editing.element === "none" ? "" : (editing.element || ""),
      elements: Array.isArray(editing.elements) ? editing.elements : [],
      stock: (editing.stock === "" || editing.stock === null || editing.stock === undefined) ? null : Number(editing.stock),
    };
    try {
      if (editing.id) await api.put(`/projects/${editing.id}`, payload);
      else await api.post("/projects", payload);
      toast.success(t("common.save")); setEditing(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const del = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/projects/${id}`); load(); };
  const clearCat = async () => {
    try {
      const r = await api.delete("/projects/clear", { params: { category: effectiveCat } });
      load();
      const bid = r.data.batch_id;
      toast.success(
        lang === "zh" ? `已清空 ${r.data.deleted} 个产品` : `Cleared ${r.data.deleted} products`,
        bid ? {
          duration: 15000,
          action: {
            label: lang === "zh" ? "撤销" : "Undo",
            onClick: async () => {
              try {
                const rr = await api.post(`/projects/restore/${bid}`);
                toast.success(lang === "zh" ? `已恢复 ${rr.data.restored} 个产品` : `Restored ${rr.data.restored} products`);
                load();
              } catch (e) { toast.error(e.response?.data?.detail || "Restore failed"); }
            },
          },
        } : undefined
      );
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const onFile = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 8 * 1024 * 1024) { toast.error(lang === "zh" ? "图片需小于 8MB" : "Image must be < 8MB"); return; }
    try {
      const dataUrl = await compressImageToDataUrl(f);
      setEditing((p) => ({ ...p, image_url: dataUrl }));
    } catch { toast.error(lang === "zh" ? "上传失败" : "Upload failed"); }
    e.target.value = "";
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4 flex-wrap gap-2">
        {!fixedCategory ? (
          <div className="flex flex-wrap gap-1.5">
            <Button size="sm" variant={catFilter === "all" ? "default" : "outline"} className={catFilter === "all" ? "bg-[hsl(var(--primary))] text-primary-foreground" : ""} onClick={() => setCatFilter("all")} data-testid="admin-cat-all">{t("adminx.allCategories")}</Button>
            {filterCats.map((c) => (
              <Button key={c} size="sm" variant={catFilter === c ? "default" : "outline"} className={catFilter === c ? "bg-[hsl(var(--primary))] text-primary-foreground" : ""} onClick={() => setCatFilter(c)} data-testid={`admin-cat-${c}`}>{t(`categories.${c}`)}{c === "merit_project" ? (lang === "zh" ? "（旧）" : " (legacy)") : ""}</Button>
            ))}
          </div>
        ) : <div className="text-sm text-muted-foreground">{shown.length} {lang === "zh" ? "个项目" : "items"}</div>}
        <div className="flex items-center gap-2">
          {effectiveCat !== "all" && shown.length > 0 && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button size="sm" variant="outline" className="border-[hsl(var(--brand-vermillion))] text-[hsl(var(--brand-vermillion))] hover:bg-[hsl(var(--brand-vermillion)/0.1)]" data-testid="admin-clear-category">
                  <Trash2 className="h-4 w-4 mr-1" />{lang === "zh" ? "清空该分类" : "Clear category"}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent data-testid="clear-category-dialog">
                <AlertDialogHeader>
                  <AlertDialogTitle>{lang === "zh" ? "清空该分类下所有产品？" : "Clear all products in this category?"}</AlertDialogTitle>
                  <AlertDialogDescription>
                    {lang === "zh"
                      ? `将永久删除「${t(`categories.${effectiveCat}`)}」分类下的全部 ${shown.length} 个产品，且无法恢复。`
                      : `This permanently deletes all ${shown.length} products in "${t(`categories.${effectiveCat}`)}" and cannot be undone.`}
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel data-testid="clear-cancel">{lang === "zh" ? "取消" : "Cancel"}</AlertDialogCancel>
                  <AlertDialogAction onClick={clearCat} className="bg-[hsl(var(--brand-vermillion))] text-white hover:opacity-90" data-testid="clear-confirm">{lang === "zh" ? "确认清空" : "Confirm clear"}</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
          <Button onClick={() => setEditing({ ...emptyProject, category: (effectiveCat === "all" || effectiveCat === "merit_project") ? "daily_merit" : effectiveCat })} data-testid="admin-add-project-button" className="bg-[hsl(var(--primary))] text-primary-foreground"><Plus className="h-4 w-4 mr-1" />{t("admin.addProject")}</Button>
        </div>
      </div>
      <div className="text-xs text-muted-foreground mb-2 flex items-center gap-1.5">
        <GripVertical className="h-3.5 w-3.5" />{lang === "zh" ? "拖拽左侧手柄可调整显示顺序" : "Drag the handle to reorder"}
      </div>
      <div className="space-y-2" data-testid="admin-project-list">
        {shown.map((p) => (
          <div
            key={p.id}
            draggable
            onDragStart={() => setDragId(p.id)}
            onDragOver={(e) => { e.preventDefault(); if (overId !== p.id) setOverId(p.id); }}
            onDragEnd={() => { setDragId(null); setOverId(null); }}
            onDrop={(e) => { e.preventDefault(); reorderTo(p.id); }}
            className={`flex items-center gap-3 rounded-xl border bg-card p-2.5 transition-colors ${overId === p.id && dragId !== p.id ? "border-[hsl(var(--brand-gold))] bg-[hsl(var(--brand-jade-mist))]" : "border-border"} ${dragId === p.id ? "opacity-50" : ""}`}
            data-testid={`admin-project-${p.id}`}
          >
            <button className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground shrink-0" aria-label="drag" data-testid={`drag-project-${p.id}`}>
              <GripVertical className="h-5 w-5" />
            </button>
            <img src={p.image_url || FALLBACK_IMG} draggable={false} onError={(e) => { e.currentTarget.src = FALLBACK_IMG; }} alt="" className="h-12 w-12 rounded-lg object-cover border border-border shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="font-semibold truncate">{p.title_zh}</div>
              <div className="text-xs text-muted-foreground flex items-center gap-1.5 flex-wrap mt-0.5">
                <span>{t(`categories.${p.category}`)}</span>
                <span>· RM{Number(p.price).toFixed(2)}</span>
                {p.subcategory && <Badge variant="outline" className="h-5 border-[hsl(var(--brand-gold))] text-[hsl(var(--brand-bronze))]">{p.subcategory}</Badge>}
                {p.promoted && <Badge className="h-5 bg-[hsl(var(--brand-vermillion))] text-white">{t("common.recommended")}</Badge>}
                {!p.active && <Badge variant="outline" className="h-5">Inactive</Badge>}
              </div>
            </div>
            <div className="flex gap-1 shrink-0">
              <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => downloadFile(`/admin/export/project/${p.id}`, `${p.title_zh}.xlsx`)} data-testid={`export-project-${p.id}`}><Download className="h-4 w-4" /></Button>
              <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setEditing({ ...p })} data-testid={`edit-project-${p.id}`}><Pencil className="h-4 w-4" /></Button>
              <Button size="icon" variant="ghost" className="h-8 w-8 text-red-600" onClick={() => del(p.id)} data-testid={`delete-project-${p.id}`}><Trash2 className="h-4 w-4" /></Button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-lg bg-card max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing?.id ? t("admin.editProject") : t("admin.addProject")}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div><Label>Category</Label>
                <Select value={editing.category} onValueChange={(v) => setEditing({ ...editing, category: v })}>
                  <SelectTrigger data-testid="project-category-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(CAT_CREATE.includes(editing.category) ? CAT_CREATE : [editing.category, ...CAT_CREATE]).map((c) => (
                      <SelectItem key={c} value={c}>{t(`categories.${c}`)}{c === "merit_project" ? (lang === "zh" ? "（旧）" : " (legacy)") : ""}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>{lang === "zh" ? "分类标签（大类下细分）" : "Category tag (sub-category)"}</Label>
                <Input list={`subcat-list-${editing.category}`} value={editing.subcategory || ""}
                  onChange={(e) => setEditing({ ...editing, subcategory: e.target.value })}
                  placeholder={lang === "zh" ? "选择或输入，如 供灯 / 放生" : "Pick or type, e.g. 供灯"} data-testid="project-subcategory" />
                <datalist id={`subcat-list-${editing.category}`}>
                  {tagSuggestions(editing.category).map((tg) => <option key={tg} value={tg} />)}
                </datalist>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div><Label>标题 (中)</Label><Input value={editing.title_zh} onChange={(e) => setEditing({ ...editing, title_zh: e.target.value })} data-testid="project-title-zh" /></div>
                <div><Label>Title (EN)</Label><Input value={editing.title_en} onChange={(e) => setEditing({ ...editing, title_en: e.target.value })} /></div>
              </div>
              <div><Label>简介 (中)</Label><Textarea rows={2} value={editing.desc_zh} onChange={(e) => setEditing({ ...editing, desc_zh: e.target.value })} /></div>
              <div><Label>Description (EN)</Label><Textarea rows={2} value={editing.desc_en} onChange={(e) => setEditing({ ...editing, desc_en: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-2">
                <div><Label>{t("common.price")} (RM)</Label><Input type="number" value={editing.price} onChange={(e) => setEditing({ ...editing, price: e.target.value })} data-testid="project-price" /></div>
                <div><Label>Sort</Label><Input type="number" value={editing.sort_order} onChange={(e) => setEditing({ ...editing, sort_order: e.target.value })} /></div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div><Label>{lang === "zh" ? "主五行（命盘推荐用）" : "Primary element"}</Label>
                  <Select value={editing.element || "none"} onValueChange={(v) => setEditing({ ...editing, element: v })}>
                    <SelectTrigger data-testid="project-element-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">{lang === "zh" ? "无 / 不指定" : "None"}</SelectItem>
                      {["金", "木", "水", "火", "土"].map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                {editing.category === "shop" && (
                  <div><Label>{lang === "zh" ? "库存数量" : "Stock"}</Label>
                    <Input type="number" min={0} value={editing.stock ?? ""} onChange={(e) => setEditing({ ...editing, stock: e.target.value })} data-testid="project-stock" placeholder={lang === "zh" ? "如 20" : "e.g. 20"} />
                  </div>
                )}
              </div>
              <div>
                <Label>{lang === "zh" ? "附加五行（可多选，命盘配对用）" : "Additional elements (multi-select)"}</Label>
                <div className="mt-1.5 flex flex-wrap gap-2" data-testid="project-elements-toggle">
                  {["金", "木", "水", "火", "土"].map((e) => {
                    const list = Array.isArray(editing.elements) ? editing.elements : [];
                    const active = list.includes(e);
                    const isPrimary = editing.element === e;
                    return (
                      <Button
                        key={e}
                        type="button"
                        size="sm"
                        variant={active || isPrimary ? "default" : "outline"}
                        className={`rounded-full h-8 w-10 ${active || isPrimary ? "bg-[hsl(var(--primary))] text-primary-foreground" : ""}`}
                        data-testid={`project-element-chip-${e}`}
                        onClick={() => {
                          const next = active ? list.filter((x) => x !== e) : [...list, e];
                          setEditing({ ...editing, elements: next });
                        }}
                      >
                        {e}
                      </Button>
                    );
                  })}
                </div>
                <p className="mt-1 text-[11px] text-muted-foreground">
                  {lang === "zh" ? "主五行会自动纳入配对；可再勾选其他相关五行以扩大命盘推荐范围。" : "The primary element is always included; pick extra elements to widen chart matching."}
                </p>
              </div>
              {editing.category === "lotus" && (
                <div><Label>Lotus Type</Label>
                  <Select value={editing.lotus_type || ""} onValueChange={(v) => setEditing({ ...editing, lotus_type: v })}>
                    <SelectTrigger><SelectValue placeholder="-" /></SelectTrigger>
                    <SelectContent>{LOTUS_TYPES.map((l) => <SelectItem key={l || "none"} value={l || "none"}>{l || "(light)"}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
              <div><Label>Image URL</Label><Input value={editing.image_url} onChange={(e) => setEditing({ ...editing, image_url: e.target.value })} placeholder="https://..." /></div>
              <div><Label>{lang === "zh" ? "或上传图片" : "Or upload image"}</Label><input type="file" accept="image/*" onChange={onFile} className="block w-full text-sm mt-1" /></div>
              {editing.image_url && <img src={editing.image_url} alt="" className="h-24 rounded-lg object-cover border border-border" />}
              <div><Label>WhatsApp Group Link</Label><Input value={editing.whatsapp_group_link} onChange={(e) => setEditing({ ...editing, whatsapp_group_link: e.target.value })} /></div>
              <div className="flex items-center gap-6">
                <div className="flex items-center gap-2"><Switch checked={editing.promoted} onCheckedChange={(v) => setEditing({ ...editing, promoted: v })} data-testid="project-promoted-switch" /><Label>{t("common.recommended")}</Label></div>
                <div className="flex items-center gap-2"><Switch checked={editing.active} onCheckedChange={(v) => setEditing({ ...editing, active: v })} /><Label>Active</Label></div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>{t("common.cancel")}</Button>
            <Button className="bg-[hsl(var(--primary))] text-primary-foreground" onClick={save} data-testid="save-project-button">{t("common.save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ---------------- Members ---------------- */
function Members() {
  const { t, lang } = useI18n();
  const { user: currentUser } = useAuth();
  const [members, setMembers] = useState([]);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [adj, setAdj] = useState({ amount: "", description: "" });
  const [creating, setCreating] = useState(null);
  const [pwd, setPwd] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [behaviourUser, setBehaviourUser] = useState(null);
  const load = useCallback((query) => { api.get("/admin/members", { params: query ? { q: query } : {} }).then((r) => setMembers(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(""); }, [load]);

  const createMember = async () => {
    if (!creating.email || !creating.password || !creating.name) { toast.error(lang === "zh" ? "请填写姓名、邮箱、密码" : "Name, email, password required"); return; }
    try {
      await api.post("/admin/members", {
        name: creating.name, email: creating.email, password: creating.password, phone: creating.phone || "",
        gender: creating.gender || "",
        birth_year: creating.birth_year ? Number(creating.birth_year) : null,
        birth_month: creating.birth_month ? Number(creating.birth_month) : null,
        birth_day: creating.birth_day ? Number(creating.birth_day) : null,
        birth_hour: creating.birth_hour !== "" && creating.birth_hour != null ? Number(creating.birth_hour) : null,
        birth_minute: Number(creating.birth_minute) || 0,
      });
      toast.success(t("common.save")); setCreating(null); load(q);
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const saveMember = async () => {
    try {
      await api.put(`/admin/members/${editing.id}`, {
        name: editing.name, phone: editing.phone, gender: editing.gender,
        birth_year: editing.birth_year ? Number(editing.birth_year) : null,
        birth_month: editing.birth_month ? Number(editing.birth_month) : null,
        birth_day: editing.birth_day ? Number(editing.birth_day) : null,
        birth_hour: editing.birth_hour !== "" && editing.birth_hour != null ? Number(editing.birth_hour) : null,
        birth_minute: Number(editing.birth_minute) || 0,
      });
      if (adj.amount) await api.post("/admin/points/adjust", { user_id: editing.id, amount: Number(adj.amount), description: adj.description || "Admin adjustment" });
      if (pwd) {
        if (pwd.length < 6) { toast.error(lang === "zh" ? "密码至少 6 位" : "Password must be at least 6 characters"); return; }
        await api.put(`/admin/members/${editing.id}/password`, { password: pwd });
      }
      const orig = members.find((x) => x.id === editing.id);
      if (orig && editing.role && editing.role !== orig.role) {
        await api.put(`/admin/members/${editing.id}/role`, { role: editing.role });
      }
      toast.success(t("common.save")); setEditing(null); setAdj({ amount: "", description: "" }); setPwd(""); load(q);
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/admin/members/${deleteTarget.id}`);
      toast.success(lang === "zh" ? "会员已删除" : "Member deleted");
      setDeleteTarget(null); load(q);
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  return (
    <div>
      <div className="flex items-center justify-between gap-2 mb-4 flex-wrap">
        <form className="flex gap-2 max-w-sm flex-1" onSubmit={(e) => { e.preventDefault(); load(q); }}>
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("common.search")} data-testid="member-search" />
          <Button type="submit" className="bg-[hsl(var(--primary))] text-primary-foreground">{t("common.search")}</Button>
        </form>
        <Button onClick={() => setCreating({ name: "", email: "", password: "", phone: "", gender: "", birth_year: "", birth_month: "", birth_day: "", birth_hour: "", birth_minute: 0 })} className="bg-[hsl(var(--brand-vermillion))] text-white" data-testid="admin-add-member-button"><Plus className="h-4 w-4 mr-1" />{lang === "zh" ? "新增会员" : "Add Member"}</Button>
      </div>
      <Card className="border-border"><CardContent className="p-0 overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>{t("auth.name")}</TableHead><TableHead>{t("auth.email")}</TableHead><TableHead>{t("auth.phone")}</TableHead>
            <TableHead>{lang === "zh" ? "生日" : "Birth"}</TableHead><TableHead>{t("common.points")}</TableHead><TableHead>{lang === "zh" ? "注册时间" : "Registered"}</TableHead><TableHead></TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {members.map((m) => (
              <TableRow key={m.id} data-testid={`admin-member-${m.id}`}>
                <TableCell className="font-semibold">{m.name}{m.role === "admin" && <Badge className="ml-1" variant="outline">admin</Badge>}</TableCell>
                <TableCell className="text-sm">{m.email}</TableCell>
                <TableCell className="text-sm">{m.phone}</TableCell>
                <TableCell className="text-xs">{m.birth_year ? `${m.birth_year}-${m.birth_month}-${m.birth_day} ${m.birth_hour}:${m.birth_minute || 0}` : "-"}</TableCell>
                <TableCell className="font-mono-num">{Number(m.points || 0).toFixed(2)}</TableCell>
                <TableCell className="text-xs text-muted-foreground" data-testid={`member-created-${m.id}`}>{m.created_at ? fmtWhen(m.created_at, lang === "zh") : "-"}</TableCell>
                <TableCell>
                  <div className="flex items-center justify-end gap-0.5">
                    {m.phone && (
                      <a href={`https://wa.me/${String(m.phone).replace(/[^0-9]/g, "")}?text=${encodeURIComponent(lang === "zh" ? "您好，这里是你好功德 CiAO Merit" : "Hello, this is CiAO Merit")}`}
                        target="_blank" rel="noopener noreferrer" title="WhatsApp" data-testid={`whatsapp-member-${m.id}`}>
                        <Button size="icon" variant="ghost" className="text-green-600 hover:text-green-700"><MessageCircle className="h-4 w-4" /></Button>
                      </a>
                    )}
                    <Button size="icon" variant="ghost" title={lang === "zh" ? "行为/路径" : "Activity"} onClick={() => setBehaviourUser(m.id)} data-testid={`view-behaviour-${m.id}`}><Eye className="h-4 w-4" /></Button>
                    <Button size="icon" variant="ghost" title={lang === "zh" ? "编辑" : "Edit"} onClick={() => setEditing({ ...m })} data-testid={`edit-member-${m.id}`}><Pencil className="h-4 w-4" /></Button>
                    <Button size="icon" variant="ghost" title={lang === "zh" ? "删除" : "Delete"} className="text-red-600 hover:text-red-700"
                      disabled={m.id === currentUser?.id} onClick={() => setDeleteTarget(m)} data-testid={`delete-member-${m.id}`}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent></Card>

      <Dialog open={!!editing} onOpenChange={(o) => { if (!o) { setEditing(null); setPwd(""); setAdj({ amount: "", description: "" }); } }}>
        <DialogContent className="max-w-md bg-card max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{lang === "zh" ? "编辑会员" : "Edit Member"}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div><Label>{t("auth.name")}</Label><Input value={editing.name || ""} onChange={(e) => setEditing({ ...editing, name: e.target.value })} /></div>
              <div><Label>{t("auth.phone")}</Label><Input value={editing.phone || ""} onChange={(e) => setEditing({ ...editing, phone: e.target.value })} /></div>
              <div className="grid grid-cols-5 gap-1">
                {["birth_year", "birth_month", "birth_day", "birth_hour", "birth_minute"].map((k) => (
                  <div key={k}><Label className="text-[10px]">{k.split("_")[1]}</Label><Input type="number" value={editing[k] ?? ""} onChange={(e) => setEditing({ ...editing, [k]: e.target.value })} /></div>
                ))}
              </div>
              <div className="rounded-lg border border-border p-3">
                <Label className="text-sm font-semibold">{lang === "zh" ? "调整功德点数" : "Adjust Points"}</Label>
                <div className="grid grid-cols-3 gap-2 mt-2">
                  <Input type="number" placeholder="+/- pts" value={adj.amount} onChange={(e) => setAdj({ ...adj, amount: e.target.value })} data-testid="points-adjust-amount" />
                  <Input className="col-span-2" placeholder={lang === "zh" ? "原因" : "Reason"} value={adj.description} onChange={(e) => setAdj({ ...adj, description: e.target.value })} />
                </div>
              </div>
              <div className="rounded-lg border border-border p-3">
                <Label className="text-sm font-semibold">{lang === "zh" ? "重置登录密码" : "Reset Login Password"}</Label>
                <p className="text-[11px] text-muted-foreground mt-0.5 mb-2">{lang === "zh" ? "留空则不修改；至少 6 位。" : "Leave blank to keep unchanged; min 6 characters."}</p>
                <Input type="text" autoComplete="new-password" placeholder={lang === "zh" ? "输入新密码" : "New password"} value={pwd} onChange={(e) => setPwd(e.target.value)} data-testid="member-password-reset" />
              </div>
              <div className="rounded-lg border border-border p-3 flex items-center justify-between">
                <div>
                  <Label className="text-sm font-semibold flex items-center gap-1.5"><ShieldCheck className="h-4 w-4" />{lang === "zh" ? "管理员权限" : "Administrator"}</Label>
                  <p className="text-[11px] text-muted-foreground mt-0.5">{lang === "zh" ? "开启后该用户可进入后台管理" : "Grants access to the admin panel"}</p>
                </div>
                <Switch
                  checked={editing.role === "admin"}
                  disabled={editing.id === currentUser?.id}
                  onCheckedChange={(v) => setEditing({ ...editing, role: v ? "admin" : "member" })}
                  data-testid="member-role-switch"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>{t("common.cancel")}</Button>
            <Button className="bg-[hsl(var(--primary))] text-primary-foreground" onClick={saveMember} data-testid="save-member-button">{t("common.save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!creating} onOpenChange={(o) => !o && setCreating(null)}>
        <DialogContent className="max-w-md bg-card max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{lang === "zh" ? "新增会员" : "Add Member"}</DialogTitle></DialogHeader>
          {creating && (
            <div className="space-y-3">
              <div><Label>{t("auth.name")}</Label><Input value={creating.name} onChange={(e) => setCreating({ ...creating, name: e.target.value })} data-testid="new-member-name" /></div>
              <div><Label>{t("auth.email")}</Label><Input type="email" value={creating.email} onChange={(e) => setCreating({ ...creating, email: e.target.value })} data-testid="new-member-email" /></div>
              <div><Label>{t("auth.password")}</Label><Input value={creating.password} onChange={(e) => setCreating({ ...creating, password: e.target.value })} data-testid="new-member-password" /></div>
              <div><Label>{t("auth.phone")}</Label><Input value={creating.phone} onChange={(e) => setCreating({ ...creating, phone: e.target.value })} data-testid="new-member-phone" /></div>
              <div>
                <Label className="text-xs">{lang === "zh" ? "阳历生辰 (年/月/日/时/分)" : "Solar birth (Y/M/D/H/Min)"}</Label>
                <div className="grid grid-cols-5 gap-1">
                  {["birth_year", "birth_month", "birth_day", "birth_hour", "birth_minute"].map((k) => (
                    <Input key={k} type="number" placeholder={k.split("_")[1]} value={creating[k] ?? ""} onChange={(e) => setCreating({ ...creating, [k]: e.target.value })} />
                  ))}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreating(null)}>{t("common.cancel")}</Button>
            <Button className="bg-[hsl(var(--primary))] text-primary-foreground" onClick={createMember} data-testid="create-member-button">{t("common.save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => { if (!o) setDeleteTarget(null); }}>
        <AlertDialogContent data-testid="delete-member-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{lang === "zh" ? "删除会员？" : "Delete member?"}</AlertDialogTitle>
            <AlertDialogDescription>
              {lang === "zh"
                ? `将删除会员「${deleteTarget?.name || ""}」的账号，历史订单与积分记录仍会保留。此操作无法撤销。`
                : `This removes ${deleteTarget?.name || "the member"}'s account. Historical orders and points records are kept. This cannot be undone.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700 text-white" onClick={confirmDelete} data-testid="confirm-delete-member">
              {lang === "zh" ? "删除" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {behaviourUser && (
        <UserUsageDialog userId={behaviourUser} range="all" onClose={() => setBehaviourUser(null)} />
      )}
    </div>
  );
}

/* ---------------- Deities ---------------- */
const emptyDeity = { name_zh: "", name_en: "", desc_zh: "", desc_en: "", image_url: "", address: "", lat: null, lng: null };
function Deities() {
  const { t, lang } = useI18n();
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = useCallback(() => { api.get("/deities").then((r) => setItems(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);
  const save = async () => {
    try {
      if (editing.id) await api.put(`/deities/${editing.id}`, editing);
      else await api.post("/deities", editing);
      toast.success(t("common.save")); setEditing(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const del = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/deities/${id}`); load(); };
  const onFile = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 8 * 1024 * 1024) { toast.error(lang === "zh" ? "图片需小于 8MB" : "Image must be < 8MB"); return; }
    try {
      const dataUrl = await compressImageToDataUrl(f);
      setEditing((p) => ({ ...p, image_url: dataUrl }));
      toast.success(lang === "zh" ? "照片已就绪，记得保存" : "Photo ready, remember to save");
    } catch { toast.error(lang === "zh" ? "上传失败" : "Upload failed"); }
    e.target.value = "";
  };
  return (
    <div>
      <div className="flex justify-end mb-4"><Button onClick={() => setEditing({ ...emptyDeity })} className="bg-[hsl(var(--primary))] text-primary-foreground" data-testid="admin-add-deity-button"><Plus className="h-4 w-4 mr-1" />{t("admin.addDeity")}</Button></div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((d) => (
          <Card key={d.id} className="border-border"><CardContent className="p-4">
            <div className="flex justify-between items-start">
              <div><div className="font-semibold">{d.name_zh}</div><div className="text-xs text-muted-foreground line-clamp-2">{d.address}</div></div>
              <div className="flex gap-1"><Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setEditing({ ...d })}><Pencil className="h-4 w-4" /></Button><Button size="icon" variant="ghost" className="h-8 w-8 text-red-600" onClick={() => del(d.id)}><Trash2 className="h-4 w-4" /></Button></div>
            </div>
          </CardContent></Card>
        ))}
      </div>
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-lg bg-card max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing?.id ? t("admin.deities") : t("admin.addDeity")}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <div><Label>名称 (中)</Label><Input value={editing.name_zh} onChange={(e) => setEditing({ ...editing, name_zh: e.target.value })} data-testid="deity-name-zh" /></div>
                <div><Label>Name (EN)</Label><Input value={editing.name_en} onChange={(e) => setEditing({ ...editing, name_en: e.target.value })} /></div>
              </div>
              <div><Label>简介 (中)</Label><Textarea rows={2} value={editing.desc_zh} onChange={(e) => setEditing({ ...editing, desc_zh: e.target.value })} /></div>
              <div><Label>Description (EN)</Label><Textarea rows={2} value={editing.desc_en} onChange={(e) => setEditing({ ...editing, desc_en: e.target.value })} /></div>
              <div><Label>{lang === "zh" ? "照片链接 (Image URL)" : "Image URL"}</Label><Input value={editing.image_url && editing.image_url.startsWith("data:") ? "" : editing.image_url} onChange={(e) => setEditing({ ...editing, image_url: e.target.value })} placeholder="https://..." data-testid="deity-image-url" /></div>
              <div><Label>{lang === "zh" ? "或上传照片" : "Or upload photo"}</Label><input type="file" accept="image/*" onChange={onFile} className="block w-full text-sm mt-1" data-testid="deity-image-file" /></div>
              {editing.image_url && <img src={editing.image_url} alt="" className="h-24 w-full rounded-lg object-cover border border-border" data-testid="deity-image-preview" />}
              <div><Label>{t("deity.address")}</Label><Textarea rows={2} value={editing.address} onChange={(e) => setEditing({ ...editing, address: e.target.value })} data-testid="deity-address" /></div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <Label>{t("adminx.locations")}</Label>
                  <Button type="button" size="sm" variant="outline" onClick={() => setEditing({ ...editing, locations: [...(editing.locations || []), { name: "", address: "", lat: null, lng: null }] })} data-testid="add-location-button"><Plus className="h-3.5 w-3.5 mr-1" />{t("adminx.addLocation")}</Button>
                </div>
                <div className="space-y-2">
                  {(editing.locations || []).map((loc, i) => (
                    <div key={i} className="rounded-lg border border-border p-2 space-y-1">
                      <div className="flex gap-2">
                        <Input placeholder={t("adminx.locName")} value={loc.name || ""} onChange={(e) => { const ls = [...editing.locations]; ls[i] = { ...ls[i], name: e.target.value }; setEditing({ ...editing, locations: ls }); }} data-testid={`loc-name-${i}`} />
                        <Button type="button" size="icon" variant="ghost" className="text-red-600 shrink-0" onClick={() => setEditing({ ...editing, locations: editing.locations.filter((_, x) => x !== i) })}><Trash2 className="h-4 w-4" /></Button>
                      </div>
                      <Input placeholder={t("adminx.locAddress")} value={loc.address || ""} onChange={(e) => { const ls = [...editing.locations]; ls[i] = { ...ls[i], address: e.target.value }; setEditing({ ...editing, locations: ls }); }} data-testid={`loc-address-${i}`} />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setEditing(null)}>{t("common.cancel")}</Button><Button className="bg-[hsl(var(--primary))] text-primary-foreground" onClick={save} data-testid="save-deity-button">{t("common.save")}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ---------------- Settings ---------------- */
function SettingsTab() {
  const { t, lang } = useI18n();
  const { settings, reloadSettings } = useSettings();
  const [s, setS] = useState(null);
  useEffect(() => { if (settings) setS(JSON.parse(JSON.stringify(settings))); }, [settings]);
  if (!s) return null;
  const bank = s.bank || {}; const wa = s.whatsapp || {}; const content = s.content || {}; const about = content.about || {}; const lotus = content.lotus || {};
  const social = s.social || {};
  const taisui = s.taisui || {};
  const tsIncludes = Array.isArray(taisui.includes) ? taisui.includes : [];
  const setBank = (k, v) => setS({ ...s, bank: { ...bank, [k]: v } });
  const setWa = (k, v) => setS({ ...s, whatsapp: { ...wa, [k]: v } });
  const setSocial = (k, v) => setS({ ...s, social: { ...social, [k]: v } });
  const setAbout = (k, v) => setS({ ...s, content: { ...content, about: { ...about, [k]: v } } });
  const setLotus = (k, v) => setS({ ...s, content: { ...content, lotus: { ...lotus, [k]: v } } });
  const setTaisui = (k, v) => setS({ ...s, taisui: { ...taisui, [k]: v } });
  const setTsItem = (i, k, v) => { const list = tsIncludes.map((it, idx) => idx === i ? { ...it, [k]: v } : it); setTaisui("includes", list); };
  const addTsItem = () => setTaisui("includes", [...tsIncludes, { title_zh: "", title_en: "", desc_zh: "", desc_en: "" }]);
  const removeTsItem = (i) => setTaisui("includes", tsIncludes.filter((_, idx) => idx !== i));

  const save = async () => {
    try {
      await api.put("/settings", { bank: s.bank, whatsapp: s.whatsapp, social: s.social, google_maps_key: s.google_maps_key, content: s.content, points: s.points, taisui: s.taisui });
      toast.success(t("common.save")); reloadSettings();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  return (
    <div className="space-y-6 max-w-2xl">
      <Card className="border-border"><CardContent className="p-5 space-y-3">
        <h3 className="font-semibold">{lang === "zh" ? "银行汇款资料" : "Bank Details"}</h3>
        <div className="grid grid-cols-2 gap-2">
          <div><Label>{t("bankin.bankName")}</Label><Input value={bank.bank_name || ""} onChange={(e) => setBank("bank_name", e.target.value)} data-testid="settings-bank-name" /></div>
          <div><Label>{t("bankin.accountName")}</Label><Input value={bank.account_name || ""} onChange={(e) => setBank("account_name", e.target.value)} /></div>
        </div>
        <div><Label>{t("bankin.accountNo")}</Label><Input value={bank.account_no || ""} onChange={(e) => setBank("account_no", e.target.value)} data-testid="settings-bank-account" /></div>
        <div><Label>汇款备注 (中)</Label><Input value={bank.note_zh || ""} onChange={(e) => setBank("note_zh", e.target.value)} /></div>
        <div><Label>Note (EN)</Label><Input value={bank.note_en || ""} onChange={(e) => setBank("note_en", e.target.value)} /></div>
        <div className="pt-1">
          <Label>{lang === "zh" ? "收款二维码 (DuitNow / 银行 QR)" : "Payment QR (DuitNow / Bank)"}</Label>
          <p className="text-[11px] text-muted-foreground mt-0.5 mb-2">{lang === "zh" ? "上传后，客户可在付款页扫码支付。" : "Customers can scan this at checkout to pay."}</p>
          <div className="flex items-start gap-3">
            {bank.qr_image ? (
              <div className="relative">
                <img src={bank.qr_image} alt="payment qr" className="h-32 w-32 rounded-lg border border-border object-contain bg-white" data-testid="settings-bank-qr-preview" />
                <Button size="icon" variant="ghost" className="absolute -top-2 -right-2 h-6 w-6 bg-card border border-border text-red-600" onClick={() => setBank("qr_image", "")} data-testid="settings-bank-qr-remove"><X className="h-3.5 w-3.5" /></Button>
              </div>
            ) : (
              <div className="h-32 w-32 rounded-lg border border-dashed border-border flex items-center justify-center text-muted-foreground"><FileImage className="h-7 w-7" /></div>
            )}
            <div>
              <input type="file" accept="image/*" id="bank-qr-upload" className="hidden"
                onChange={async (e) => {
                  const f = e.target.files?.[0]; if (!f) return;
                  try { const dataUrl = await compressImageToDataUrl(f, 800, 0.85); setBank("qr_image", dataUrl); }
                  catch (err) { toast.error(lang === "zh" ? "图片处理失败" : "Image failed"); }
                }} data-testid="settings-bank-qr-input" />
              <Button type="button" variant="outline" size="sm" onClick={() => document.getElementById("bank-qr-upload").click()} data-testid="settings-bank-qr-upload">
                <Upload className="h-4 w-4 mr-1.5" />{lang === "zh" ? "上传二维码" : "Upload QR"}
              </Button>
            </div>
          </div>
        </div>
      </CardContent></Card>

      <Card className="border-border"><CardContent className="p-5 space-y-3">
        <h3 className="font-semibold">WhatsApp</h3>
        <div><Label>{lang === "zh" ? "客服号码 (如 60123456789)" : "Contact number"}</Label><Input value={wa.contact_number || ""} onChange={(e) => setWa("contact_number", e.target.value)} data-testid="settings-wa-number" /></div>
        <div><Label>{lang === "zh" ? "群组链接" : "Group link"}</Label><Input value={wa.group_link || ""} onChange={(e) => setWa("group_link", e.target.value)} data-testid="settings-wa-group" /></div>
      </CardContent></Card>

      <Card className="border-border"><CardContent className="p-5 space-y-3">
        <h3 className="font-semibold">{lang === "zh" ? "社群链接（页脚“关注我们”）" : "Social Links (footer)"}</h3>
        <p className="text-xs text-muted-foreground">{lang === "zh" ? "填写完整链接后，页脚会显示对应图标；留空则不显示。" : "Filled links appear as footer icons; blank ones are hidden."}</p>
        <div><Label>Facebook</Label><Input value={social.facebook || ""} onChange={(e) => setSocial("facebook", e.target.value)} placeholder="https://facebook.com/..." data-testid="settings-social-facebook" /></div>
        <div><Label>Instagram</Label><Input value={social.instagram || ""} onChange={(e) => setSocial("instagram", e.target.value)} placeholder="https://instagram.com/..." data-testid="settings-social-instagram" /></div>
        <div><Label>TikTok</Label><Input value={social.tiktok || ""} onChange={(e) => setSocial("tiktok", e.target.value)} placeholder="https://tiktok.com/@..." data-testid="settings-social-tiktok" /></div>
      </CardContent></Card>

      <Card className="border-border"><CardContent className="p-5 space-y-3">
        <h3 className="font-semibold">{lang === "zh" ? "简介页文案" : "About Page Content"}</h3>
        <div><Label>简介 (中)</Label><Textarea rows={2} value={about.intro_zh || ""} onChange={(e) => setAbout("intro_zh", e.target.value)} /></div>
        <div><Label>Intro (EN)</Label><Textarea rows={2} value={about.intro_en || ""} onChange={(e) => setAbout("intro_en", e.target.value)} /></div>
        <div><Label>功德的意义 (中)</Label><Textarea rows={2} value={about.meaning_zh || ""} onChange={(e) => setAbout("meaning_zh", e.target.value)} /></div>
        <div><Label>Meaning (EN)</Label><Textarea rows={2} value={about.meaning_en || ""} onChange={(e) => setAbout("meaning_en", e.target.value)} /></div>
        <div><Label>一份爱心一份温暖 (中)</Label><Textarea rows={2} value={about.warmth_zh || ""} onChange={(e) => setAbout("warmth_zh", e.target.value)} /></div>
        <div><Label>Warmth (EN)</Label><Textarea rows={2} value={about.warmth_en || ""} onChange={(e) => setAbout("warmth_en", e.target.value)} /></div>
      </CardContent></Card>

      <Card className="border-border"><CardContent className="p-5 space-y-3">
        <h3 className="font-semibold">{lang === "zh" ? "莲花页文案" : "Lotus Page"}</h3>
        <div><Label>折莲意义 (中)</Label><Textarea rows={2} value={lotus.meaning_zh || ""} onChange={(e) => setLotus("meaning_zh", e.target.value)} /></div>
        <div><Label>Meaning (EN)</Label><Textarea rows={2} value={lotus.meaning_en || ""} onChange={(e) => setLotus("meaning_en", e.target.value)} /></div>
      </CardContent></Card>

      <Card className="border-border" data-testid="settings-taisui"><CardContent className="p-5 space-y-3">
        <h3 className="font-semibold">{lang === "zh" ? "犯太岁配套设置" : "Tai Sui Package Settings"}</h3>
        <div className="max-w-xs">
          <Label>{lang === "zh" ? "自动提醒提前天数（立春前）" : "Auto-reminder days before 立春"}</Label>
          <Input type="number" min={1} max={120} value={taisui.renewal_window_days ?? 21}
            onChange={(e) => setTaisui("renewal_window_days", e.target.value === "" ? "" : Number(e.target.value))}
            data-testid="settings-taisui-window" />
          <p className="text-[11px] text-muted-foreground mt-1">{lang === "zh" ? "系统会在立春前这段天数内，自动邮件提醒明年犯太岁的会员（每年一次）。" : "The auto email reminder fires within this many days before 立春 each year."}</p>
        </div>
        <div className="pt-1">
          <div className="flex items-center justify-between mb-1">
            <Label>{lang === "zh" ? "化太岁配套内容（会员弹窗显示）" : "Package ritual items (shown to members)"}</Label>
            <Button type="button" size="sm" variant="outline" className="h-7" onClick={addTsItem} data-testid="settings-taisui-add"><Plus className="h-3.5 w-3.5 mr-1" />{lang === "zh" ? "添加" : "Add"}</Button>
          </div>
          <p className="text-[11px] text-muted-foreground mb-2">{lang === "zh" ? "留空则使用系统默认内容。" : "Leave empty to use the built-in default items."}</p>
          <div className="space-y-3">
            {tsIncludes.map((it, i) => (
              <div key={i} className="rounded-lg border border-border p-3 space-y-2" data-testid={`settings-taisui-item-${i}`}>
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-muted-foreground">#{i + 1}</span>
                  <Button type="button" size="icon" variant="ghost" className="h-7 w-7 text-red-600" onClick={() => removeTsItem(i)} data-testid={`settings-taisui-remove-${i}`}><Trash2 className="h-4 w-4" /></Button>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <Input placeholder={lang === "zh" ? "标题 (中)" : "Title ZH"} value={it.title_zh || ""} onChange={(e) => setTsItem(i, "title_zh", e.target.value)} />
                  <Input placeholder="Title (EN)" value={it.title_en || ""} onChange={(e) => setTsItem(i, "title_en", e.target.value)} />
                </div>
                <Textarea rows={2} placeholder={lang === "zh" ? "说明 (中)" : "Description ZH"} value={it.desc_zh || ""} onChange={(e) => setTsItem(i, "desc_zh", e.target.value)} />
                <Textarea rows={2} placeholder="Description (EN)" value={it.desc_en || ""} onChange={(e) => setTsItem(i, "desc_en", e.target.value)} />
              </div>
            ))}
          </div>
        </div>
      </CardContent></Card>

      <Button className="bg-[hsl(var(--primary))] text-primary-foreground" onClick={save} data-testid="save-settings-button">{t("common.save")}</Button>
    </div>
  );
}

/* ---------------- Testimonials Admin ---------------- */
function TestimonialsAdmin() {
  const { t, lang } = useI18n();
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("pending");
  const load = useCallback(() => { api.get("/admin/testimonials", { params: status === "all" ? {} : { status } }).then((r) => setItems(r.data)).catch(() => {}); }, [status]);
  useEffect(() => { load(); }, [load]);
  const act = async (id, st) => { try { await api.put(`/admin/testimonials/${id}`, { status: st }); toast.success("OK"); load(); } catch (e) { toast.error("Error"); } };
  const del = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/admin/testimonials/${id}`); load(); };
  const sColor = { pending: "bg-amber-100 text-amber-800", approved: "bg-green-100 text-green-800", rejected: "bg-red-100 text-red-700" };
  return (
    <div>
      <div className="flex gap-2 mb-4">
        {["pending", "approved", "rejected", "all"].map((s) => (
          <Button key={s} size="sm" variant={status === s ? "default" : "outline"} className={status === s ? "bg-[hsl(var(--primary))] text-primary-foreground" : ""} onClick={() => setStatus(s)} data-testid={`testi-filter-${s}`}>
            {s === "all" ? (lang === "zh" ? "全部" : "All") : t(`adminx.status${s.charAt(0).toUpperCase() + s.slice(1)}`)}
          </Button>
        ))}
      </div>
      <div className="space-y-3">
        {items.map((it) => (
          <Card key={it.id} className="border-border" data-testid={`admin-testi-${it.id}`}>
            <CardContent className="p-4 flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0">
                <div className="font-semibold">{it.name} <Badge className={sColor[it.status]}>{t(`adminx.status${it.status.charAt(0).toUpperCase() + it.status.slice(1)}`)}</Badge></div>
                <p className="mt-1 text-sm text-foreground/85">{it.message}</p>
                <div className="text-xs text-muted-foreground mt-1">{(it.created_at || "").slice(0, 16).replace("T", " ")}</div>
              </div>
              <div className="flex gap-1 shrink-0">
                {it.status !== "approved" && <Button size="sm" variant="outline" className="text-green-700 border-green-300" onClick={() => act(it.id, "approved")} data-testid={`testi-approve-${it.id}`}><Check className="h-4 w-4 mr-1" />{t("admin.approve")}</Button>}
                {it.status !== "rejected" && <Button size="sm" variant="outline" className="text-red-600 border-red-300" onClick={() => act(it.id, "rejected")} data-testid={`testi-reject-${it.id}`}><X className="h-4 w-4 mr-1" />{t("admin.reject")}</Button>}
                <Button size="icon" variant="ghost" className="h-8 w-8 text-red-600" onClick={() => del(it.id)}><Trash2 className="h-4 w-4" /></Button>
              </div>
            </CardContent>
          </Card>
        ))}
        {items.length === 0 && <div className="text-center py-10 text-muted-foreground">{lang === "zh" ? "暂无见证" : "No testimonials"}</div>}
      </div>
    </div>
  );
}

/* ---------------- Lotus Admin (content + videos) ---------------- */
function LotusAdmin() {
  const { t, lang } = useI18n();
  const { settings, reloadSettings } = useSettings();
  const [content, setContent] = useState(null);
  useEffect(() => { if (settings) setContent(JSON.parse(JSON.stringify(settings.content || {}))); }, [settings]);
  if (!content) return null;
  const lotus = content.lotus || {};
  const videos = lotus.videos || [];
  const setLotus = (k, v) => setContent({ ...content, lotus: { ...lotus, [k]: v } });
  const setVideos = (vs) => setContent({ ...content, lotus: { ...lotus, videos: vs } });
  const save = async () => { try { await api.put("/settings", { content }); toast.success(t("common.save")); reloadSettings(); } catch (e) { toast.error("Error"); } };
  return (
    <div className="space-y-6">
      <Card className="border-border"><CardContent className="p-5 space-y-3">
        <h3 className="font-semibold">{t("adminx.lotusMeaning")}</h3>
        <div><Label>折莲意义 (中)</Label><Textarea rows={2} value={lotus.meaning_zh || ""} onChange={(e) => setLotus("meaning_zh", e.target.value)} data-testid="lotus-meaning-zh" /></div>
        <div><Label>Meaning (EN)</Label><Textarea rows={2} value={lotus.meaning_en || ""} onChange={(e) => setLotus("meaning_en", e.target.value)} /></div>
      </CardContent></Card>

      <Card className="border-border"><CardContent className="p-5 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold">{t("adminx.videos")}</h3>
          <Button size="sm" variant="outline" onClick={() => setVideos([...videos, { title_zh: "", title_en: "", url: "" }])} data-testid="add-video-button"><Plus className="h-4 w-4 mr-1" />{t("adminx.addVideo")}</Button>
        </div>
        <p className="text-[11px] text-muted-foreground -mt-1">{lang === "zh" ? "支持直接粘贴 TikTok 视频链接（抖音国际版）或 YouTube 链接，系统会自动嵌入播放。" : "Paste a TikTok or YouTube video link — it will embed automatically."}</p>
        {videos.map((v, i) => (
          <div key={i} className="rounded-lg border border-border p-3 space-y-2">
            <div className="flex gap-2">
              <Input placeholder={t("adminx.videoTitle")} value={v.title_zh || ""} onChange={(e) => { const vs = [...videos]; vs[i] = { ...vs[i], title_zh: e.target.value }; setVideos(vs); }} data-testid={`video-title-zh-${i}`} />
              <Input placeholder={t("adminx.videoTitleEn")} value={v.title_en || ""} onChange={(e) => { const vs = [...videos]; vs[i] = { ...vs[i], title_en: e.target.value }; setVideos(vs); }} />
              <Button size="icon" variant="ghost" className="text-red-600 shrink-0" onClick={() => setVideos(videos.filter((_, x) => x !== i))}><Trash2 className="h-4 w-4" /></Button>
            </div>
            <Input placeholder={lang === "zh" ? "粘贴 TikTok / YouTube 链接" : "Paste TikTok / YouTube link"} value={v.url || ""} onChange={(e) => { const vs = [...videos]; vs[i] = { ...vs[i], url: e.target.value }; setVideos(vs); }} data-testid={`video-url-${i}`} />
          </div>
        ))}
      </CardContent></Card>

      <Button className="bg-[hsl(var(--primary))] text-primary-foreground" onClick={save} data-testid="save-lotus-button">{t("common.save")}</Button>

      <Card className="border-border"><CardContent className="p-5">
        <h3 className="font-semibold mb-3">{lang === "zh" ? "莲花项目（销售 / 课程 / 祈福）— 可增删改" : "Lotus Projects (sales / class / blessing)"}</h3>
        <ProjectManager fixedCategory="lotus" />
      </CardContent></Card>
    </div>
  );
}

/* ---------------- FAQ Admin (常见问题) ---------------- */
const emptyFaq = { question_zh: "", question_en: "", answer_zh: "", answer_en: "", sort_order: 0, active: true };
function FaqAdmin() {
  const { t, lang } = useI18n();
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = useCallback(() => { api.get("/faqs", { params: { include_inactive: true } }).then((r) => setItems(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!editing.question_zh?.trim() || !editing.answer_zh?.trim()) { toast.error(lang === "zh" ? "请填写中文问题与答案" : "Chinese question & answer required"); return; }
    const payload = { ...editing, sort_order: Number(editing.sort_order) || 0 };
    try {
      if (editing.id) await api.put(`/faqs/${editing.id}`, payload);
      else await api.post("/faqs", payload);
      toast.success(t("common.save")); setEditing(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const del = async (id) => { if (!window.confirm(t("faqx.deleteConfirm"))) return; try { await api.delete(`/faqs/${id}`); load(); } catch (e) { toast.error("Error"); } };

  return (
    <div>
      <div className="flex justify-between items-center mb-4 flex-wrap gap-2">
        <div className="text-sm text-muted-foreground">{items.length} {lang === "zh" ? "条问题" : "questions"}</div>
        <Button onClick={() => setEditing({ ...emptyFaq, sort_order: (items.length + 1) })} className="bg-[hsl(var(--primary))] text-primary-foreground" data-testid="admin-add-faq-button">
          <Plus className="h-4 w-4 mr-1" />{t("faqx.add")}
        </Button>
      </div>

      {items.length === 0 ? (
        <div className="text-center py-14 text-muted-foreground">{t("faqx.emptyAdmin")}</div>
      ) : (
        <div className="space-y-3">
          {items.map((it) => (
            <Card key={it.id} className="border-border" data-testid={`admin-faq-row-${it.id}`}>
              <CardContent className="p-4 flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-mono-num text-[hsl(var(--brand-bronze))]">#{it.sort_order}</span>
                    <span className="font-semibold">{it.question_zh}</span>
                    {!it.active && <Badge variant="outline">Inactive</Badge>}
                  </div>
                  {it.question_en && <div className="text-xs text-muted-foreground mt-0.5">{it.question_en}</div>}
                  <p className="mt-1 text-sm text-foreground/80 line-clamp-2">{it.answer_zh}</p>
                </div>
                <div className="flex gap-1 shrink-0">
                  <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setEditing({ ...it })} data-testid={`edit-faq-${it.id}`}><Pencil className="h-4 w-4" /></Button>
                  <Button size="icon" variant="ghost" className="h-8 w-8 text-red-600" onClick={() => del(it.id)} data-testid={`delete-faq-${it.id}`}><Trash2 className="h-4 w-4" /></Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-lg bg-card max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing?.id ? t("faqx.edit") : t("faqx.add")}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div><Label>{t("faqx.questionZh")}</Label><Input value={editing.question_zh} onChange={(e) => setEditing({ ...editing, question_zh: e.target.value })} data-testid="faq-question-zh" /></div>
              <div><Label>{t("faqx.questionEn")}</Label><Input value={editing.question_en || ""} onChange={(e) => setEditing({ ...editing, question_en: e.target.value })} data-testid="faq-question-en" /></div>
              <div><Label>{t("faqx.answerZh")}</Label><Textarea rows={3} value={editing.answer_zh} onChange={(e) => setEditing({ ...editing, answer_zh: e.target.value })} data-testid="faq-answer-zh" /></div>
              <div><Label>{t("faqx.answerEn")}</Label><Textarea rows={3} value={editing.answer_en || ""} onChange={(e) => setEditing({ ...editing, answer_en: e.target.value })} data-testid="faq-answer-en" /></div>
              <div className="flex items-center gap-6">
                <div className="w-28"><Label>{t("faqx.sortOrder")}</Label><Input type="number" value={editing.sort_order} onChange={(e) => setEditing({ ...editing, sort_order: e.target.value })} data-testid="faq-sort-order" /></div>
                <div className="flex items-center gap-2 pt-5"><Switch checked={editing.active} onCheckedChange={(v) => setEditing({ ...editing, active: v })} data-testid="faq-active-switch" /><Label>{t("faqx.active")}</Label></div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>{t("common.cancel")}</Button>
            <Button className="bg-[hsl(var(--primary))] text-primary-foreground" onClick={save} data-testid="save-faq-button">{t("common.save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ---------------- System: Admin accounts + Data maintenance ---------------- */
function SystemTab() {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const [admins, setAdmins] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/admins").then((r) => setAdmins(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const addAdmin = async () => {
    if (!form.name.trim() || !form.email.trim() || form.password.length < 6) {
      toast.error(lang === "zh" ? "请填写姓名、邮箱，密码至少 6 位" : "Enter name, email, and a 6+ char password");
      return;
    }
    try {
      await api.post("/admin/admins", form);
      toast.success(t("adminx.adminAdded"));
      setForm({ name: "", email: "", password: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const delAdmin = async (id) => {
    try { await api.delete(`/admin/admins/${id}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const cleanup = async () => {
    setBusy(true);
    try {
      const r = await api.post("/admin/cleanup-test-data");
      const d = r.data?.deleted || {};
      toast.success(`${t("adminx.cleanupDone")} · ${lang === "zh" ? "会员" : "members"} ${d.members || 0} · ${lang === "zh" ? "订单" : "orders"} ${d.orders || 0}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6">
      {/* Admin accounts */}
      <Card className="border-border" data-testid="admin-accounts-card"><CardContent className="p-4">
        <div className="flex items-center gap-2 font-semibold">
          <ShieldCheck className="h-5 w-5 text-[hsl(var(--brand-bronze))]" />{t("adminx.adminAccounts")}
        </div>
        <p className="text-xs text-muted-foreground mt-1 mb-4">{t("adminx.adminAccountsDesc")}</p>

        <div className="grid sm:grid-cols-4 gap-2 items-end mb-4">
          <div className="sm:col-span-1"><Label>{t("auth.name")}</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="admin-new-name" /></div>
          <div className="sm:col-span-1"><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="admin-new-email" /></div>
          <div className="sm:col-span-1"><Label>{t("auth.password")}</Label><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="admin-new-password" /></div>
          <Button onClick={addAdmin} className="bg-[hsl(var(--primary))] text-primary-foreground" data-testid="admin-add-admin-button"><UserPlus className="h-4 w-4 mr-1" />{t("adminx.addAdmin")}</Button>
        </div>

        <div className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow>
              <TableHead>{t("auth.name")}</TableHead><TableHead>Email</TableHead><TableHead className="text-right"> </TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {admins.map((a) => (
                <TableRow key={a.id} data-testid={`admin-row-${a.id}`}>
                  <TableCell className="font-semibold">{a.name}{a.id === user?.id && <Badge variant="outline" className="ml-2">{lang === "zh" ? "当前" : "You"}</Badge>}</TableCell>
                  <TableCell className="text-muted-foreground">{a.email}</TableCell>
                  <TableCell className="text-right">
                    <Button size="icon" variant="ghost" className="h-8 w-8 text-red-600" disabled={a.id === user?.id || admins.length <= 1} onClick={() => delAdmin(a.id)} data-testid={`delete-admin-${a.id}`}><Trash2 className="h-4 w-4" /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent></Card>

      {/* Danger zone: cleanup */}
      <Card className="border-[hsl(var(--brand-vermillion))]/40 bg-[hsl(var(--brand-vermillion))]/5" data-testid="cleanup-card"><CardContent className="p-4">
        <div className="flex items-center gap-2 font-semibold text-[hsl(var(--brand-vermillion))]">
          <AlertTriangle className="h-5 w-5" />{t("adminx.dataMaintenance")}
        </div>
        <p className="text-sm text-foreground/80 mt-2">{t("adminx.cleanupDesc")}</p>
        <ul className="text-xs text-muted-foreground mt-2 list-disc pl-5 space-y-0.5">
          <li>{t("adminx.cleanupDelete")}</li>
          <li>{t("adminx.cleanupKeep")}</li>
        </ul>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="outline" className="mt-4 border-[hsl(var(--brand-vermillion))] text-[hsl(var(--brand-vermillion))]" disabled={busy} data-testid="cleanup-open-button">
              <Trash2 className="h-4 w-4 mr-1" />{t("adminx.cleanupButton")}
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent className="bg-card">
            <AlertDialogHeader>
              <AlertDialogTitle>{t("adminx.cleanupConfirmTitle")}</AlertDialogTitle>
              <AlertDialogDescription>{t("adminx.cleanupConfirmDesc")}</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="cleanup-cancel">{t("common.cancel")}</AlertDialogCancel>
              <AlertDialogAction className="bg-[hsl(var(--brand-vermillion))] text-white hover:opacity-90" onClick={cleanup} data-testid="cleanup-confirm">{t("adminx.cleanupConfirmButton")}</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </CardContent></Card>
    </div>
  );
}

const TAG_CATS = ["daily_merit", "charity", "lotus", "service", "shop"];

function TagManager() {
  const { t, lang } = useI18n();
  const { tags: allTags, reload } = useTags();
  const [cat, setCat] = useState("daily_merit");
  const [newName, setNewName] = useState("");
  const [dragId, setDragId] = useState(null);
  const [overId, setOverId] = useState(null);
  const [nameEdits, setNameEdits] = useState({});

  const cats = TAG_CATS.concat(allTags.some((x) => x.category === "merit_project") ? ["merit_project"] : []);
  const list = allTags.filter((x) => x.category === cat).slice().sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));

  const patch = async (tag, changes) => {
    try {
      await api.put(`/tags/${tag.id}`, {
        category: tag.category,
        name: (changes.name ?? tag.name),
        color: (changes.color ?? tag.color) || "",
        icon: (changes.icon ?? tag.icon) || "",
      });
      toast.success(lang === "zh" ? "已保存" : "Saved");
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); reload(); }
  };

  const add = async () => {
    const name = newName.trim();
    if (!name) return;
    try { await api.post("/tags", { category: cat, name }); setNewName(""); toast.success(lang === "zh" ? "已添加" : "Added"); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const remove = async (tag) => {
    try {
      const r = await api.delete(`/tags/${tag.id}`);
      toast.success((lang === "zh" ? "已删除，已从 " : "Deleted, cleared from ") + (r.data.cleared || 0) + (lang === "zh" ? " 个产品移除该标签" : " products"));
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const reorderTo = async (targetId) => {
    const fromId = dragId; setDragId(null); setOverId(null);
    if (!fromId || fromId === targetId) return;
    const arr = [...list];
    const f = arr.findIndex((x) => x.id === fromId);
    const tt = arr.findIndex((x) => x.id === targetId);
    if (f < 0 || tt < 0) return;
    const [m] = arr.splice(f, 1); arr.splice(tt, 0, m);
    const orders = arr.map((x, i) => ({ id: x.id, sort_order: (i + 1) * 10 }));
    try { await api.put("/tags/reorder", { orders }); reload(); } catch (e) { toast.error("Error"); reload(); }
  };

  return (
    <div className="space-y-4 max-w-2xl" data-testid="tag-manager">
      <div className="flex flex-wrap gap-2">
        {cats.map((c) => (
          <Button key={c} size="sm" variant={cat === c ? "default" : "outline"} className={cat === c ? "bg-[hsl(var(--primary))] text-primary-foreground" : ""} onClick={() => setCat(c)} data-testid={`tagcat-${c}`}>
            {t(`categories.${c}`)}{c === "merit_project" ? (lang === "zh" ? "（旧）" : " (legacy)") : ""}
          </Button>
        ))}
      </div>

      <div className="flex gap-2">
        <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder={lang === "zh" ? "新标签名称，如 供灯" : "New tag, e.g. 供灯"} onKeyDown={(e) => e.key === "Enter" && add()} data-testid="tag-new-name" />
        <Button onClick={add} className="bg-[hsl(var(--primary))] text-primary-foreground shrink-0" data-testid="tag-add-btn"><Plus className="h-4 w-4 mr-1" />{lang === "zh" ? "添加标签" : "Add tag"}</Button>
      </div>

      <div className="text-xs text-muted-foreground flex items-center gap-1.5"><GripVertical className="h-3.5 w-3.5" />{lang === "zh" ? "拖拽调整标签在页面上的先后顺序，颜色/图标即选即存" : "Drag to set the order tags appear; colour/icon save instantly"}</div>

      {list.length === 0 ? (
        <div className="py-8 text-center text-sm text-muted-foreground" data-testid="tag-list-empty">{lang === "zh" ? "该分类暂无标签" : "No tags yet"}</div>
      ) : (
        <div className="space-y-2" data-testid="tag-list">
          {list.map((tag) => (
            <div
              key={tag.id}
              draggable
              onDragStart={() => setDragId(tag.id)}
              onDragOver={(e) => { e.preventDefault(); if (overId !== tag.id) setOverId(tag.id); }}
              onDragEnd={() => { setDragId(null); setOverId(null); }}
              onDrop={(e) => { e.preventDefault(); reorderTo(tag.id); }}
              className={`flex items-center gap-2 rounded-xl border bg-card p-2.5 ${overId === tag.id && dragId !== tag.id ? "border-[hsl(var(--brand-gold))] bg-[hsl(var(--brand-jade-mist))]" : "border-border"} ${dragId === tag.id ? "opacity-50" : ""}`}
              data-testid={`tag-row-${tag.id}`}
            >
              <button className="cursor-grab active:cursor-grabbing text-muted-foreground shrink-0" data-testid={`tag-drag-${tag.id}`}><GripVertical className="h-5 w-5" /></button>
              <Popover>
                <PopoverTrigger asChild>
                  <button className="h-7 w-7 rounded-full border-2 shrink-0" style={{ backgroundColor: tag.color || "#a8843b", borderColor: "rgba(0,0,0,0.15)" }} aria-label="colour" data-testid={`tag-color-${tag.id}`} />
                </PopoverTrigger>
                <PopoverContent className="w-auto p-2">
                  <div className="grid grid-cols-4 gap-2">
                    {TAG_COLORS.map((c) => (
                      <button key={c} className="h-7 w-7 rounded-full border-2" style={{ backgroundColor: c, borderColor: tag.color === c ? "#000" : "rgba(0,0,0,0.15)" }} onClick={() => patch(tag, { color: c })} data-testid={`tag-swatch-${c}`} />
                    ))}
                  </div>
                </PopoverContent>
              </Popover>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="icon" className="h-8 w-8 shrink-0" data-testid={`tag-icon-${tag.id}`}>{tag.icon ? <TagIcon name={tag.icon} className="h-4 w-4" /> : <span className="text-xs text-muted-foreground">—</span>}</Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-2">
                  <div className="grid grid-cols-6 gap-1.5" style={{ maxWidth: 240 }}>
                    <button className="h-8 w-8 rounded flex items-center justify-center border text-xs" onClick={() => patch(tag, { icon: "" })}>—</button>
                    {ICON_OPTIONS.map((ic) => (
                      <button key={ic} className={`h-8 w-8 rounded flex items-center justify-center border ${tag.icon === ic ? "bg-[hsl(var(--brand-jade-mist))] border-[hsl(var(--brand-gold))]" : ""}`} onClick={() => patch(tag, { icon: ic })} data-testid={`tag-iconopt-${ic}`}><TagIcon name={ic} className="h-4 w-4" /></button>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>
              <Input
                className="flex-1 min-w-0"
                value={nameEdits[tag.id] ?? tag.name}
                onChange={(e) => setNameEdits({ ...nameEdits, [tag.id]: e.target.value })}
                onBlur={() => { const v = (nameEdits[tag.id] ?? tag.name).trim(); if (v && v !== tag.name) patch(tag, { name: v }); }}
                onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
                data-testid={`tag-name-${tag.id}`}
              />
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-red-600 shrink-0" data-testid={`tag-del-${tag.id}`}><Trash2 className="h-4 w-4" /></Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>{lang === "zh" ? `删除标签「${tag.name}」？` : `Delete tag "${tag.name}"?`}</AlertDialogTitle>
                    <AlertDialogDescription>{lang === "zh" ? "该标签会从所有使用它的产品上移除（产品本身不受影响）。" : "This tag will be removed from all products that use it (the products themselves stay)."}</AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>{lang === "zh" ? "取消" : "Cancel"}</AlertDialogCancel>
                    <AlertDialogAction onClick={() => remove(tag)} className="bg-red-600 hover:bg-red-700">{lang === "zh" ? "删除" : "Delete"}</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function EmailAdmin() {
  const { t, lang } = useI18n();
  const [configured, setConfigured] = useState(null);
  const [to, setTo] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [sched, setSched] = useState(null);
  const [nextRuns, setNextRuns] = useState({});
  const [savingSched, setSavingSched] = useState(false);
  const [logs, setLogs] = useState([]);
  const [viewRecips, setViewRecips] = useState(null);
  const [unsubs, setUnsubs] = useState([]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewHtml, setPreviewHtml] = useState("");
  const [previewBusy, setPreviewBusy] = useState(false);

  const loadSchedule = useCallback(() => {
    api.get("/admin/email/schedule").then((r) => {
      setSched(r.data.schedule); setNextRuns(r.data.next_runs || {}); setConfigured(r.data.configured);
    }).catch(() => {});
  }, []);
  const loadLogs = useCallback(() => {
    api.get("/admin/email/logs").then((r) => setLogs(r.data || [])).catch(() => {});
  }, []);
  const loadUnsubs = useCallback(() => {
    api.get("/admin/email/unsubscribed").then((r) => setUnsubs(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    api.get("/email/status").then((r) => setConfigured(r.data.configured)).catch(() => setConfigured(false));
    loadSchedule(); loadLogs(); loadUnsubs();
  }, [loadSchedule, loadLogs, loadUnsubs]);

  const preview = async (kind) => {
    setPreviewBusy(true); setPreviewHtml(""); setPreviewOpen(true);
    try {
      const r = await api.get("/admin/email/preview", { params: { kind, lang } });
      setPreviewHtml(r.data.html || "");
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); setPreviewOpen(false); }
    finally { setPreviewBusy(false); }
  };

  const testSend = async () => {
    if (!to) { toast.error(lang === "zh" ? "请输入邮箱" : "Enter an email"); return; }
    setBusy(true);
    try {
      const r = await api.post("/admin/email/test", { to, lang });
      if (r.data.configured === false) toast.info(t("emailx.notConfigured"));
      else if (r.data.ok) toast.success(t("emailx.sent"));
      else toast.error(r.data.detail || t("emailx.sendFail"));
      loadLogs();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };

  const broadcast = async (kind) => {
    if (!window.confirm(lang === "zh" ? `确认向所有已订阅会员发送${kind === "daily" ? "每日" : "每周"}运程邮件？` : `Send ${kind} fortune email to all subscribed members?`)) return;
    setBusy(true); setResult(null);
    try {
      const r = await api.post(`/admin/email/broadcast/${kind}`);
      if (r.data.configured === false) {
        toast.info(t("emailx.notConfigured"));
      } else {
        toast.success(lang === "zh" ? "群发已开始，稍后请查看下方发送记录" : "Broadcast started — check the send log below shortly");
        // the broadcast runs in the background; refresh the log as it completes
        setTimeout(loadLogs, 6000);
        setTimeout(loadLogs, 18000);
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };

  const saveSchedule = async () => {
    if (!sched) return;
    setSavingSched(true);
    try {
      const r = await api.put("/admin/email/schedule", sched);
      setSched(r.data.schedule); setNextRuns(r.data.next_runs || {});
      toast.success(t("adminx.emailScheduleSaved"));
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setSavingSched(false); }
  };

  const WD = lang === "zh"
    ? ["周一", "周二", "周三", "周四", "周五", "周六", "周日"]
    : ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  const fmt = (iso) => (iso ? String(iso).slice(0, 16).replace("T", " ") : "—");
  const kindLabel = (k) => (k === "weekly" ? t("adminx.kindWeekly") : k === "test" ? t("adminx.kindTest") : t("adminx.kindDaily"));
  const trigLabel = (g) => (g === "scheduled" ? t("adminx.triggerScheduled") : g === "admin-test" ? t("adminx.triggerAdminTest") : t("adminx.triggerManual"));
  const stColor = { sent: "bg-green-100 text-green-800", failed: "bg-red-100 text-red-700", skipped: "bg-amber-100 text-amber-800" };

  return (
    <div className="space-y-5 max-w-2xl" data-testid="admin-email-tab">
      <Card className={configured ? "border-green-600/40" : "border-[hsl(var(--brand-gold))]"}>
        <CardContent className="p-5">
          <div className="flex items-center gap-2 mb-2"><Mail className="h-5 w-5 text-[hsl(var(--brand-bronze))]" /><h3 className="font-semibold">{t("adminx.emailMgmt")}</h3>
            <Badge className={configured ? "bg-green-100 text-green-800 ml-2" : "bg-amber-100 text-amber-800 ml-2"} data-testid="admin-email-status">
              {configured === null ? "…" : configured ? t("adminx.emailConfigured") : t("adminx.emailNotConfigured")}
            </Badge>
          </div>
          {!configured && <p className="text-xs text-muted-foreground leading-6">{t("adminx.emailSetupHint")}</p>}
        </CardContent>
      </Card>

      <Card className="border-border"><CardContent className="p-5 space-y-3">
        <div className="flex items-center gap-2"><Send className="h-4 w-4 text-[hsl(var(--brand-bronze))]" /><h4 className="font-semibold">{t("adminx.emailTestTitle")}</h4></div>
        <p className="text-xs text-muted-foreground">{t("adminx.emailTestDesc")}</p>
        <div className="flex gap-2 flex-wrap">
          <Input type="email" placeholder="test@example.com" value={to} onChange={(e) => setTo(e.target.value)} data-testid="admin-email-test-input" className="max-w-xs" />
          <Button className="bg-[hsl(var(--primary))] text-primary-foreground" disabled={busy} onClick={testSend} data-testid="admin-email-test-send">{t("adminx.emailTestBtn")}</Button>
        </div>
      </CardContent></Card>

      <Card className="border-border"><CardContent className="p-5 space-y-3">
        <div className="flex items-center gap-2"><Megaphone className="h-4 w-4 text-[hsl(var(--brand-bronze))]" /><h4 className="font-semibold">{t("adminx.emailBroadcastTitle")}</h4></div>
        <p className="text-xs text-muted-foreground">{t("adminx.emailBroadcastDesc")}</p>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" className="border-[hsl(var(--brand-gold))]" disabled={busy} onClick={() => broadcast("daily")} data-testid="admin-email-broadcast-daily">{t("adminx.emailBroadcastDaily")}</Button>
          <Button variant="outline" className="border-[hsl(var(--brand-gold))]" disabled={busy} onClick={() => broadcast("weekly")} data-testid="admin-email-broadcast-weekly">{t("adminx.emailBroadcastWeekly")}</Button>
          <div className="w-px bg-border mx-1" />
          <Button variant="ghost" className="text-[hsl(var(--brand-bronze))]" onClick={() => preview("daily")} data-testid="admin-email-preview-daily"><Eye className="h-4 w-4 mr-1" />{t("adminx.emailPreview")} · {t("adminx.kindDaily")}</Button>
          <Button variant="ghost" className="text-[hsl(var(--brand-bronze))]" onClick={() => preview("weekly")} data-testid="admin-email-preview-weekly"><Eye className="h-4 w-4 mr-1" />{t("adminx.emailPreview")} · {t("adminx.kindWeekly")}</Button>
        </div>
        {result && (
          <div className="text-sm rounded-lg bg-[hsl(var(--secondary))] p-3 font-mono-num" data-testid="admin-email-broadcast-result">
            {t("adminx.emailSent")}: {result.sent} · {t("adminx.emailFailed")}: {result.failed} · {t("adminx.emailSkipped")}: {result.skipped} / {result.total_members}
          </div>
        )}
      </CardContent></Card>

      {/* Auto-send schedule */}
      <Card className="border-border"><CardContent className="p-5 space-y-4">
        <div className="flex items-center gap-2"><Clock className="h-4 w-4 text-[hsl(var(--brand-bronze))]" /><h4 className="font-semibold">{t("adminx.emailScheduleTitle")}</h4></div>
        <p className="text-xs text-muted-foreground">{t("adminx.emailScheduleDesc")}</p>
        {sched && (
          <div className="space-y-4" data-testid="admin-email-schedule">
            <div className="flex items-center gap-3">
              <Switch checked={!!sched.enabled} onCheckedChange={(v) => setSched({ ...sched, enabled: v })} data-testid="email-sched-enabled" />
              <Label className="cursor-pointer">{t("adminx.emailEnabled")}</Label>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label className="text-xs">{t("adminx.emailDailyTime")}</Label>
                <div className="flex items-center gap-1.5 mt-1">
                  <Input type="number" min="0" max="23" className="w-20 font-mono-num" value={sched.daily_hour}
                    onChange={(e) => setSched({ ...sched, daily_hour: Math.max(0, Math.min(23, Number(e.target.value) || 0)) })} data-testid="email-daily-hour" />
                  <span className="font-bold">:</span>
                  <Input type="number" min="0" max="59" className="w-20 font-mono-num" value={sched.daily_minute}
                    onChange={(e) => setSched({ ...sched, daily_minute: Math.max(0, Math.min(59, Number(e.target.value) || 0)) })} data-testid="email-daily-minute" />
                </div>
                <div className="text-[11px] text-muted-foreground mt-1">{t("adminx.emailNextRun")}: <span className="font-mono-num">{fmt(nextRuns.daily_fortune)}</span></div>
              </div>
              <div>
                <Label className="text-xs">{t("adminx.emailWeeklyTime")}</Label>
                <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                  <Select value={String(sched.weekly_day)} onValueChange={(v) => setSched({ ...sched, weekly_day: Number(v) })}>
                    <SelectTrigger className="w-28" data-testid="email-weekly-day"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {WD.map((d, i) => <SelectItem key={i} value={String(i)}>{d}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Input type="number" min="0" max="23" className="w-20 font-mono-num" value={sched.weekly_hour}
                    onChange={(e) => setSched({ ...sched, weekly_hour: Math.max(0, Math.min(23, Number(e.target.value) || 0)) })} data-testid="email-weekly-hour" />
                  <span className="font-bold">:</span>
                  <Input type="number" min="0" max="59" className="w-20 font-mono-num" value={sched.weekly_minute}
                    onChange={(e) => setSched({ ...sched, weekly_minute: Math.max(0, Math.min(59, Number(e.target.value) || 0)) })} data-testid="email-weekly-minute" />
                </div>
                <div className="text-[11px] text-muted-foreground mt-1">{t("adminx.emailNextRun")}: <span className="font-mono-num">{fmt(nextRuns.weekly_fortune)}</span></div>
              </div>
            </div>
            <Button onClick={saveSchedule} disabled={savingSched} className="bg-[hsl(var(--primary))] text-primary-foreground" data-testid="email-save-schedule">
              {t("adminx.emailSaveSchedule")}
            </Button>
          </div>
        )}
      </CardContent></Card>

      {/* Send log */}
      <Card className="border-border"><CardContent className="p-5 space-y-3">
        <div className="flex items-center gap-2"><FileImage className="h-4 w-4 text-[hsl(var(--brand-bronze))]" /><h4 className="font-semibold">{t("adminx.emailLogTitle")}</h4></div>
        <p className="text-xs text-muted-foreground">{t("adminx.emailLogDesc")}</p>
        {logs.length === 0 ? (
          <div className="py-6 text-center text-sm text-muted-foreground" data-testid="admin-email-log-empty">{t("adminx.emailLogEmpty")}</div>
        ) : (
          <div className="overflow-x-auto">
            <Table data-testid="admin-email-log-table">
              <TableHeader>
                <TableRow>
                  <TableHead>{t("adminx.emailLogTime")}</TableHead>
                  <TableHead>{t("adminx.emailLogKind")}</TableHead>
                  <TableHead>{t("adminx.emailLogTrigger")}</TableHead>
                  <TableHead className="text-center">{t("adminx.emailSent")}</TableHead>
                  <TableHead className="text-center">{t("adminx.emailFailed")}</TableHead>
                  <TableHead className="text-center">{t("adminx.emailSkipped")}</TableHead>
                  <TableHead className="text-right"> </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {logs.map((lg) => (
                  <TableRow key={lg.id} data-testid={`email-log-row-${lg.id}`}>
                    <TableCell className="font-mono-num text-xs whitespace-nowrap">{fmt(lg.created_at)}</TableCell>
                    <TableCell><Badge variant="outline">{kindLabel(lg.kind)}</Badge></TableCell>
                    <TableCell className="text-xs">{trigLabel(lg.trigger)}</TableCell>
                    <TableCell className="text-center font-mono-num text-green-700">{lg.sent}</TableCell>
                    <TableCell className="text-center font-mono-num text-red-700">{lg.failed}</TableCell>
                    <TableCell className="text-center font-mono-num text-amber-700">{lg.skipped}</TableCell>
                    <TableCell className="text-right">
                      {(lg.recipients || []).length > 0 && (
                        <Button variant="ghost" size="sm" className="h-8 text-[hsl(var(--brand-bronze))]" onClick={() => setViewRecips(lg)} data-testid={`email-log-recips-${lg.id}`}>
                          <Eye className="h-3.5 w-3.5 mr-1" />{t("adminx.emailLogRecipients")}
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent></Card>

      {/* Unsubscribed members */}
      <Card className="border-border"><CardContent className="p-5 space-y-3">
        <div className="flex items-center gap-2"><Ban className="h-4 w-4 text-[hsl(var(--brand-bronze))]" /><h4 className="font-semibold">{t("adminx.emailUnsubTitle")}</h4>
          {unsubs.length > 0 && <Badge className="bg-amber-100 text-amber-800 ml-1">{unsubs.length}</Badge>}
        </div>
        <p className="text-xs text-muted-foreground">{t("adminx.emailUnsubDesc")}</p>
        {unsubs.length === 0 ? (
          <div className="py-6 text-center text-sm text-muted-foreground" data-testid="admin-email-unsub-empty">{t("adminx.emailUnsubEmpty")}</div>
        ) : (
          <div className="overflow-x-auto">
            <Table data-testid="admin-email-unsub-table">
              <TableHeader>
                <TableRow>
                  <TableHead>{t("auth.name")}</TableHead>
                  <TableHead>{t("auth.email")}</TableHead>
                  <TableHead className="text-right">{t("adminx.emailUnsubWhen")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {unsubs.map((m) => (
                  <TableRow key={m.id} data-testid={`email-unsub-row-${m.id}`}>
                    <TableCell className="font-medium">{m.name}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{m.email}</TableCell>
                    <TableCell className="text-right font-mono-num text-xs whitespace-nowrap">{fmt(m.unsubscribed_at)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent></Card>

      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{t("adminx.emailPreviewTitle")}</DialogTitle></DialogHeader>
          {previewBusy ? (
            <div className="py-16 text-center text-muted-foreground">{t("common.loading")}</div>
          ) : (
            <iframe title="email-preview" srcDoc={previewHtml} className="w-full h-[70vh] rounded-lg border border-border bg-white" data-testid="email-preview-frame" />
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!viewRecips} onOpenChange={(o) => !o && setViewRecips(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{t("adminx.emailLogRecipients")}</DialogTitle></DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto space-y-1.5" data-testid="email-recips-list">
            {(viewRecips?.recipients || []).map((r, i) => (
              <div key={i} className="flex items-center justify-between gap-2 text-sm border-b border-border py-1.5">
                <div className="min-w-0">
                  <div className="truncate font-medium">{r.email}</div>
                  {r.name && <div className="text-xs text-muted-foreground truncate">{r.name}</div>}
                </div>
                <Badge className={stColor[r.status] || ""}>{t(`adminx.email${r.status.charAt(0).toUpperCase() + r.status.slice(1)}`)}</Badge>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ---------------- Recycle Bin (回收站) ---------------- */
function TrashManager() {
  const { t, lang } = useI18n();
  const [batches, setBatches] = useState(null);
  const load = useCallback(() => {
    api.get("/projects/trash").then((r) => setBatches(r.data.batches)).catch(() => setBatches([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const restore = async (id) => {
    try {
      const r = await api.post(`/projects/restore/${id}`);
      toast.success(lang === "zh" ? `已恢复 ${r.data.restored} 个产品` : `Restored ${r.data.restored} products`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const purge = async (id) => {
    try {
      await api.delete(`/projects/trash/${id}`);
      toast.success(lang === "zh" ? "已永久删除该批次" : "Batch permanently deleted");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  return (
    <div className="space-y-4" data-testid="admin-trash">
      <div className="flex items-center gap-2">
        <span className="grid h-8 w-8 place-items-center rounded-full bg-[hsl(var(--brand-gold)/0.15)] text-[hsl(var(--brand-bronze))]"><Recycle className="h-4 w-4" /></span>
        <div>
          <div className="font-display text-lg font-semibold">{lang === "zh" ? "回收站" : "Recycle Bin"}</div>
          <div className="text-xs text-muted-foreground">{lang === "zh" ? "分类清空产生的产品批次，可恢复或永久删除。" : "Cleared-category product batches — restore or permanently delete."}</div>
        </div>
      </div>

      {batches === null ? (
        <div className="py-16 text-center text-muted-foreground">{lang === "zh" ? "加载中…" : "Loading…"}</div>
      ) : batches.length === 0 ? (
        <div className="py-16 text-center text-muted-foreground text-sm" data-testid="trash-empty">{lang === "zh" ? "回收站是空的。" : "The recycle bin is empty."}</div>
      ) : (
        <Card className="border-border"><CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader><TableRow>
              <TableHead>{lang === "zh" ? "分类" : "Category"}</TableHead>
              <TableHead>{lang === "zh" ? "产品" : "Products"}</TableHead>
              <TableHead className="text-center">{lang === "zh" ? "数量" : "Count"}</TableHead>
              <TableHead>{lang === "zh" ? "清空时间" : "Deleted at"}</TableHead>
              <TableHead className="text-right">{lang === "zh" ? "操作" : "Action"}</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {batches.map((b) => (
                <TableRow key={b.batch_id} data-testid={`trash-row-${b.batch_id}`}>
                  <TableCell><Badge variant="outline" className="border-[hsl(var(--brand-gold))] text-[hsl(var(--brand-bronze))]">{t(`categories.${b.category}`) || b.category}</Badge></TableCell>
                  <TableCell className="max-w-xs">
                    <div className="text-xs text-muted-foreground truncate">{(b.titles || []).join("、") || "—"}</div>
                  </TableCell>
                  <TableCell className="text-center font-mono-num font-bold">{b.count}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{(b.deleted_at || "").slice(0, 16).replace("T", " ")}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button size="sm" variant="outline" className="h-8 border-[hsl(var(--brand-gold))] text-[hsl(var(--brand-bronze))]" onClick={() => restore(b.batch_id)} data-testid={`trash-restore-${b.batch_id}`}>
                        <RotateCcw className="h-3.5 w-3.5 mr-1" />{lang === "zh" ? "恢复" : "Restore"}
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button size="sm" variant="outline" className="h-8 border-[hsl(var(--brand-vermillion))] text-[hsl(var(--brand-vermillion))] hover:bg-[hsl(var(--brand-vermillion)/0.1)]" data-testid={`trash-purge-${b.batch_id}`}>
                            <Trash2 className="h-3.5 w-3.5 mr-1" />{lang === "zh" ? "永久删除" : "Delete"}
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent data-testid={`trash-purge-dialog-${b.batch_id}`}>
                          <AlertDialogHeader>
                            <AlertDialogTitle>{lang === "zh" ? "永久删除该批次？" : "Permanently delete this batch?"}</AlertDialogTitle>
                            <AlertDialogDescription>
                              {lang === "zh"
                                ? `将永久移除「${t(`categories.${b.category}`) || b.category}」批次的 ${b.count} 个产品备份，删除后无法再恢复。`
                                : `This permanently removes the backup of ${b.count} products in "${t(`categories.${b.category}`) || b.category}". It cannot be restored afterwards.`}
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel data-testid="trash-purge-cancel">{lang === "zh" ? "取消" : "Cancel"}</AlertDialogCancel>
                            <AlertDialogAction onClick={() => purge(b.batch_id)} className="bg-[hsl(var(--brand-vermillion))] text-white hover:opacity-90" data-testid="trash-purge-confirm">{lang === "zh" ? "确认永久删除" : "Confirm delete"}</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent></Card>
      )}
    </div>
  );
}


const ADMIN_SECTION_KEYS = ["overview", "insights", "usage", "orders", "members", "projects", "tags", "trash", "deities", "lotus", "testimonials", "faq", "email", "system", "settings"];

export default function Admin() {
  const { t, lang } = useI18n();
  const [active, setActive] = useState(() => {
    try {
      const saved = localStorage.getItem("ciao_admin_section");
      return saved && ADMIN_SECTION_KEYS.includes(saved) ? saved : "overview";
    } catch (e) { return "overview"; }
  });
  const [pendingCount, setPendingCount] = useState(0);

  // remember the last section the admin viewed
  useEffect(() => {
    try { localStorage.setItem("ciao_admin_section", active); } catch (e) { /* ignore */ }
  }, [active]);

  const loadPending = useCallback(() => {
    api.get("/admin/pending-orders-count").then((r) => setPendingCount(r.data.count || 0)).catch(() => {});
  }, []);
  // refresh on mount and whenever the admin switches section (e.g. after handling orders)
  useEffect(() => { loadPending(); }, [active, loadPending]);

  const L = (zh, en) => (lang === "zh" ? zh : en);
  const NAV = [
    { key: "data", label: L("数据", "Data"), icon: PieChartIcon, items: [
      { key: "overview", label: L("总览", "Summary"), icon: LayoutDashboard },
      { key: "insights", label: L("数据分析", "Analysis"), icon: BarChart3 },
      { key: "usage", label: L("使用情况", "Usage"), icon: Activity },
    ] },
    { key: "ops", label: L("运营", "Operations"), icon: ShoppingBag, items: [
      { key: "orders", label: t("admin.orders"), icon: Inbox },
      { key: "members", label: t("admin.members"), icon: Users },
    ] },
    { key: "content", label: L("内容", "Content"), icon: Layers, items: [
      { key: "projects", label: t("admin.projects"), icon: Layers },
      { key: "tags", label: t("adminx.tagsMgmt"), icon: Tag },
      { key: "trash", label: L("回收站", "Recycle Bin"), icon: Recycle },
      { key: "deities", label: t("admin.deities"), icon: Star },
      { key: "lotus", label: t("adminx.lotusMgmt"), icon: Flower2 },
      { key: "testimonials", label: t("adminx.testimonials"), icon: MessageCircle },
      { key: "faq", label: t("adminx.faq"), icon: HelpCircle },
    ] },
    { key: "system", label: L("系统", "System"), icon: Settings, items: [
      { key: "email", label: t("adminx.emailTab"), icon: Mail },
      { key: "system", label: t("adminx.system"), icon: Server },
      { key: "settings", label: t("admin.settings"), icon: Settings },
    ] },
  ];

  const PAGES = {
    overview: <Overview onNavigate={setActive} />,
    insights: <Insights />,
    usage: <UsageAnalytics />,
    orders: <Orders onChanged={loadPending} />,
    projects: <ProjectManager />,
    trash: <TrashManager />,
    tags: <TagManager />,
    members: <Members />,
    deities: <Deities />,
    lotus: <LotusAdmin />,
    testimonials: <TestimonialsAdmin />,
    faq: <FaqAdmin />,
    system: <SystemTab />,
    email: <EmailAdmin />,
    settings: <SettingsTab />,
  };

  const activeItem = NAV.flatMap((g) => g.items).find((it) => it.key === active);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <h1 className="font-display text-2xl sm:text-3xl font-bold mb-6">{t("admin.title")}</h1>

      {/* mobile grouped selector */}
      <div className="md:hidden mb-5">
        <Select value={active} onValueChange={setActive}>
          <SelectTrigger data-testid="admin-nav-mobile">
            <SelectValue>{activeItem ? activeItem.label : ""}</SelectValue>
          </SelectTrigger>
          <SelectContent>
            {NAV.map((g) => (
              <SelectGroup key={g.key}>
                <SelectLabel>{g.label}</SelectLabel>
                {g.items.map((it) => (
                  <SelectItem key={it.key} value={it.key} data-testid={`admin-nav-opt-${it.key}`}>
                    {it.label}{it.key === "orders" && pendingCount > 0 ? `（${pendingCount}）` : ""}
                  </SelectItem>
                ))}
              </SelectGroup>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="flex gap-6">
        {/* desktop grouped sidebar */}
        <aside className="hidden md:block w-52 shrink-0">
          <nav className="space-y-5 sticky top-24" data-testid="admin-sidebar">
            {NAV.map((g) => (
              <div key={g.key}>
                <div className="px-3 mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  <g.icon className="h-3.5 w-3.5" />{g.label}
                </div>
                <div className="space-y-0.5">
                  {g.items.map((it) => {
                    const on = active === it.key;
                    return (
                      <button
                        key={it.key}
                        onClick={() => setActive(it.key)}
                        data-testid={`admin-tab-${it.key}`}
                        className={`w-full flex items-center gap-2 rounded-lg px-3 py-2 text-sm transition-colors ${on
                          ? "bg-[hsl(var(--primary))] text-primary-foreground font-medium"
                          : "text-foreground/80 hover:bg-[hsl(var(--secondary))]"}`}>
                        <it.icon className="h-4 w-4 shrink-0" />
                        <span className="truncate">{it.label}</span>
                        {it.key === "orders" && pendingCount > 0 && (
                          <span
                            className={`ml-auto shrink-0 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[11px] font-bold ${on ? "bg-white/90 text-[hsl(var(--brand-vermillion))]" : "bg-[hsl(var(--brand-vermillion))] text-white"}`}
                            data-testid="admin-orders-badge">
                            {pendingCount}
                          </span>
                        )}
                        {on && it.key !== "orders" && <ChevronRight className="h-4 w-4 ml-auto shrink-0" />}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </nav>
        </aside>

        {/* content */}
        <main className="flex-1 min-w-0" data-testid="admin-content">
          {PAGES[active]}
        </main>
      </div>
    </div>
  );
}
